import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { forwardRef, useId, createContext, useRef, useCallback, useEffect, useContext, useState, Children, useMemo, useSyncExternalStore, cloneElement } from 'react';
import { clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { cva } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import { DirectionProvider, useDirection } from '@radix-ui/react-direction';
import { getLocaleDirection, hexToHsv, parseHex, formatHex, rgbToHsv, hsvToHex, clamp, hsvToHsl, resolveColumns, cellToString, hslToHsv, MATCH_RULE_ACTIVITY_METRICS, MATCH_RULE_ACTIVITY_OPS } from '@syncanix/shared';
export { buildExportFilename, slugify, toCsv, toJson, toTranscriptJson, toTranscriptMarkdown, toTsv, toTxt } from '@syncanix/shared';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as TabsPrimitive from '@radix-ui/react-tabs';

// src/components/Accordion/Accordion.tsx
var twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        {
          text: [
            "display",
            "metric",
            "metric-sm",
            "h1",
            "h2",
            "h3",
            "body",
            "small",
            "micro",
            "label",
            "code"
          ]
        }
      ],
      "font-weight": [{ font: ["control", "heading"] }],
      rounded: [{ rounded: ["xs"] }]
    }
  }
});
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var Accordion = AccordionPrimitive.Root;
var AccordionItem = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Item,
  {
    ref,
    className: cn("border-b border-hairline", className),
    ...props
  }
));
AccordionItem.displayName = "AccordionItem";
function ChevronIcon() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 16 16",
      className: "size-4 shrink-0 text-muted-foreground transition-transform duration-200",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      children: /* @__PURE__ */ jsx("path", { d: "m4 6 4 4 4-4", strokeLinecap: "round", strokeLinejoin: "round" })
    }
  );
}
var AccordionTrigger = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(AccordionPrimitive.Header, { className: "flex", children: /* @__PURE__ */ jsxs(
  AccordionPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex flex-1 items-center justify-between gap-2 rounded-sm py-3.5 text-start text-sm font-control text-foreground transition-colors hover:text-primary-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring [&[data-state=open]>svg]:rotate-180",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(ChevronIcon, {})
    ]
  }
) }));
AccordionTrigger.displayName = "AccordionTrigger";
var AccordionContent = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Content,
  {
    ref,
    className: "overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
    ...props,
    children: /* @__PURE__ */ jsx("div", { className: cn("pb-4 pt-0", className), children })
  }
));
AccordionContent.displayName = "AccordionContent";
var badgeVariants = cva(
  // Bedrock chips are RECTANGULAR on the 4px `chips` radius, not pills. A pill
  // reads as a tag you can dismiss; a chip reads as a state the row is IN,
  // which is what every one of these actually is. 12.5px at the 520 control
  // weight so it holds its own beside 14px body without shouting.
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-xs px-[9px] py-[3px] text-[12.5px] font-control leading-[16px]",
  {
    variants: {
      tone: {
        // The neutral chip is the one that carries a hairline: with no tint to
        // define it, an edge is all it has.
        neutral: "border border-hairline bg-surface-well text-muted-foreground",
        // The brand-tinted chip — "this one", "yours", "current". Distinct
        // from `info`, which reports a FACT about the row; `accent` marks the
        // row the operator is standing on. It exists because five surfaces had
        // each hand-rolled `bg-primary/10 text-primary-strong` with three
        // different paddings and a hard-coded 11px, so the one meaning the
        // palette had no tone for was the one drifting fastest.
        //
        // `text-primary-strong` on the tint measures 5.24:1 (Daylight) and
        // 8.95:1 (Midnight) — the brand FILL would be 3.1:1, which is why the
        // ink is the strong step and not `primary`.
        accent: "bg-primary-subtle text-primary-strong",
        success: "bg-success/[0.12] text-success-foreground",
        warning: "bg-warning/[0.14] text-warning-foreground",
        danger: "bg-danger/[0.11] text-danger-foreground",
        info: "bg-info/[0.10] text-info-foreground"
      }
    },
    defaultVariants: {
      tone: "neutral"
    }
  }
);
var Badge = forwardRef(
  ({ className, tone, ...props }, ref) => /* @__PURE__ */ jsx("span", { ref, className: cn(badgeVariants({ tone }), className), ...props })
);
Badge.displayName = "Badge";
var Bdi = forwardRef(
  ({ className, dir = "auto", ...props }, ref) => {
    return /* @__PURE__ */ jsx("bdi", { ref, dir, className: cn("[unicode-bidi:isolate]", className), ...props });
  }
);
Bdi.displayName = "Bdi";
var inputVariants = cva(
  // Bedrock field: 38px tall on the `controls` radius, painted on `surface`
  // rather than the page. The inset top highlight is the whole reason a field
  // reads as something you type INTO — without it a bordered box on a white
  // panel is indistinguishable from a bordered box you press.
  "flex h-[38px] w-full rounded-sm border bg-surface px-3 text-sm text-foreground shadow-[inset_0_1px_2px_rgba(10,12,17,0.04)] transition-colors placeholder:text-tertiary focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/20 disabled:cursor-not-allowed disabled:bg-surface-well disabled:text-tertiary dark:shadow-none",
  {
    variants: {
      state: {
        // `hairline-strong`, not `border`: a field's edge is the affordance,
        // so it is the one hairline in the system that is allowed to be seen.
        default: "border-hairline-strong",
        // The error state tints its own fill as well as its edge. A red border
        // alone is easy to miss in a column of eight fields; a tinted field is
        // findable by scanning, which is how people actually re-find the one
        // that failed.
        error: "border-destructive bg-destructive/[0.04] focus-visible:border-destructive focus-visible:ring-destructive/25"
      }
    },
    defaultVariants: {
      state: "default"
    }
  }
);
var Input = forwardRef(
  ({ className, label, labelClassName, error, disabled, ...props }, ref) => {
    const generatedId = useId();
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
      /* @__PURE__ */ jsx(
        "label",
        {
          htmlFor: generatedId,
          className: cn(
            "text-[13px] font-control leading-none text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
            labelClassName
          ),
          children: label
        }
      ),
      /* @__PURE__ */ jsx(
        "input",
        {
          id: generatedId,
          ref,
          disabled,
          "aria-invalid": error !== void 0 ? true : void 0,
          "aria-describedby": error !== void 0 ? `${generatedId}-error` : void 0,
          className: cn(
            inputVariants({ state: error !== void 0 ? "error" : "default" }),
            className
          ),
          ...props
        }
      ),
      error !== void 0 && /* @__PURE__ */ jsx(
        "p",
        {
          id: `${generatedId}-error`,
          role: "alert",
          className: "text-[12.5px] leading-[17px] text-danger-foreground",
          children: error
        }
      )
    ] });
  }
);
Input.displayName = "Input";
function AudiencePicker({
  options,
  value,
  onChange,
  multiple = true,
  labels,
  state = "ready",
  className
}) {
  const [query, setQuery] = useState("");
  const groupId = useId();
  if (state === "loading") {
    return /* @__PURE__ */ jsxs("div", { role: "status", "aria-busy": "true", className: cn("space-y-2", className), children: [
      /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: labels.loading }),
      /* @__PURE__ */ jsx("div", { className: "h-8 animate-pulse rounded-md bg-muted" }),
      /* @__PURE__ */ jsx("div", { className: "h-8 animate-pulse rounded-md bg-muted" })
    ] });
  }
  if (state === "error") {
    return /* @__PURE__ */ jsx("p", { role: "status", className: cn("text-sm text-danger-foreground", className), children: labels.error });
  }
  if (options.length === 0) {
    return /* @__PURE__ */ jsx("p", { className: cn("text-sm text-muted-foreground", className), children: labels.empty });
  }
  const filtered = query.trim() === "" ? options : options.filter((option) => option.name.toLowerCase().includes(query.trim().toLowerCase()));
  const toggle = (option) => {
    if (option.disabled === true) return;
    if (!multiple) {
      onChange(value.includes(option.id) ? [] : [option.id]);
      return;
    }
    onChange(
      value.includes(option.id) ? value.filter((id) => id !== option.id) : [...value, option.id]
    );
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2", className), children: [
    /* @__PURE__ */ jsx(
      Input,
      {
        type: "search",
        autoComplete: "off",
        label: labels.searchLabel,
        placeholder: labels.searchPlaceholder,
        value: query,
        onChange: (event) => {
          setQuery(event.target.value);
        }
      }
    ),
    filtered.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: labels.noMatches }) : /* @__PURE__ */ jsx("ul", { className: "max-h-64 space-y-1 overflow-y-auto", children: filtered.map((option) => {
      const inputId = `${groupId}-${option.id}`;
      return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
        "label",
        {
          htmlFor: inputId,
          className: cn(
            "flex cursor-pointer items-start gap-2 rounded-md border border-border p-2 hover:bg-muted",
            option.disabled === true && "cursor-not-allowed opacity-60 hover:bg-transparent",
            value.includes(option.id) && "border-primary/40 bg-primary-subtle"
          ),
          children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                id: inputId,
                type: "checkbox",
                className: "mt-0.5 accent-primary",
                checked: value.includes(option.id),
                disabled: option.disabled === true,
                onChange: () => {
                  toggle(option);
                }
              }
            ),
            /* @__PURE__ */ jsxs("span", { className: "min-w-0 flex-1", children: [
              /* @__PURE__ */ jsxs("span", { className: "flex flex-wrap items-center gap-2", children: [
                /* @__PURE__ */ jsx(Bdi, { className: "truncate text-sm font-medium", children: option.name }),
                /* @__PURE__ */ jsx(Badge, { tone: option.governance === true ? "info" : "neutral", children: option.governance === true ? labels.governanceBadge : labels.targetingBadge })
              ] }),
              option.description !== void 0 && /* @__PURE__ */ jsx(Bdi, { className: "block truncate text-xs text-muted-foreground", children: option.description })
            ] })
          ]
        }
      ) }, option.id);
    }) })
  ] });
}

// src/components/Logo/logo-paths.ts
var PATH_OUTER = "M605.0 282.0 C619.0 281.0 634.8 281.0 649.0 282.0 C663.2 283.0 676.8 285.2 690.0 288.0 C703.2 290.8 714.7 294.0 728.0 299.0 C741.3 304.0 753.7 308.2 770.0 318.0 C786.3 327.8 811.7 346.0 826.0 358.0 C840.3 370.0 846.5 378.0 856.0 390.0 C865.5 402.0 875.0 415.7 883.0 430.0 C891.0 444.3 898.5 461.0 904.0 476.0 C909.5 491.0 913.2 506.8 916.0 520.0 C918.8 533.2 920.2 541.3 921.0 555.0 C921.8 568.7 922.2 586.7 921.0 602.0 C919.8 617.3 917.7 631.7 914.0 647.0 C910.3 662.3 904.0 681.0 899.0 694.0 C894.0 707.0 890.5 714.0 884.0 725.0 C877.5 736.0 868.0 748.3 860.0 760.0 C849.0 741.7 841.7 724.7 827.0 705.0 C812.3 685.3 790.2 660.2 772.0 642.0 C753.8 623.8 742.5 614.7 718.0 596.0 C693.5 577.3 656.0 552.0 625.0 530.0 C625.0 528.7 625.0 527.3 625.0 526.0 C627.3 525.7 604.0 519.8 632.0 525.0 C660.0 530.2 739.3 546.3 793.0 557.0 C791.7 548.3 791.8 540.3 789.0 531.0 C786.2 521.7 780.8 509.8 776.0 501.0 C771.2 492.2 767.2 486.2 760.0 478.0 C752.8 469.8 743.3 459.8 733.0 452.0 C722.7 444.2 708.7 436.2 698.0 431.0 C687.3 425.8 678.0 423.3 669.0 421.0 C660.0 418.7 654.3 417.5 644.0 417.0 C633.7 416.5 617.0 417.0 607.0 418.0 C597.0 419.0 594.0 419.7 584.0 423.0 C574.0 426.3 557.2 432.8 547.0 438.0 C536.8 443.2 532.0 446.5 523.0 454.0 C514.0 461.5 503.0 473.3 493.0 483.0 C480.7 463.7 467.2 445.0 456.0 425.0 C444.8 405.0 436.0 383.7 426.0 363.0 C427.0 361.0 421.8 363.0 429.0 357.0 C436.2 351.0 454.8 335.8 469.0 327.0 C483.2 318.2 498.0 310.5 514.0 304.0 C530.0 297.5 549.8 291.7 565.0 288.0 C580.2 284.3 591.0 283.0 605.0 282.0 Z";
var PATH_INNER = "M387.0 406.0 C398.3 426.0 408.5 447.3 421.0 466.0 C433.5 484.7 446.0 500.7 462.0 518.0 C478.0 535.3 491.2 549.3 517.0 570.0 C542.8 590.7 583.7 618.0 617.0 642.0 C616.7 643.7 616.3 645.3 616.0 647.0 C613.7 647.0 635.0 652.2 609.0 647.0 C583.0 641.8 509.7 626.3 460.0 616.0 C460.7 622.3 459.3 626.2 462.0 635.0 C464.7 643.8 470.3 658.5 476.0 669.0 C481.7 679.5 489.3 689.8 496.0 698.0 C502.7 706.2 508.7 711.8 516.0 718.0 C523.3 724.2 530.5 729.7 540.0 735.0 C549.5 740.3 560.8 746.2 573.0 750.0 C585.2 753.8 600.8 756.8 613.0 758.0 C625.2 759.2 636.0 758.2 646.0 757.0 C656.0 755.8 663.0 754.3 673.0 751.0 C683.0 747.7 696.5 742.0 706.0 737.0 C715.5 732.0 721.2 728.3 730.0 721.0 C738.8 713.7 749.3 702.3 759.0 693.0 C771.0 710.0 783.8 726.3 795.0 744.0 C806.2 761.7 815.7 780.7 826.0 799.0 C807.7 812.7 785.5 830.5 771.0 840.0 C756.5 849.5 750.0 851.3 739.0 856.0 C728.0 860.7 721.0 864.3 705.0 868.0 C689.0 871.7 661.8 876.7 643.0 878.0 C624.2 879.3 606.3 877.5 592.0 876.0 C577.7 874.5 568.7 871.3 557.0 869.0 C533.7 878.7 505.8 891.3 487.0 898.0 C468.2 904.7 456.8 906.7 444.0 909.0 C431.2 911.3 421.3 911.0 410.0 912.0 C409.7 910.3 409.3 908.7 409.0 907.0 C416.7 891.3 427.2 872.2 432.0 860.0 C436.8 847.8 436.8 842.2 438.0 834.0 C439.2 825.8 438.7 818.7 439.0 811.0 C425.0 795.7 409.8 782.0 397.0 765.0 C384.2 748.0 371.3 728.0 362.0 709.0 C352.7 690.0 346.0 670.3 341.0 651.0 C336.0 631.7 333.3 609.8 332.0 593.0 C330.7 576.2 331.2 565.8 333.0 550.0 C334.8 534.2 337.7 516.0 343.0 498.0 C348.3 480.0 357.7 457.3 365.0 442.0 C372.3 426.7 379.7 418.0 387.0 406.0 Z";
var MARK_VIEWBOX = "0 0 1254 1254";
var MARK_VIEWBOX_TIGHT = "311.01 281.13 631 631";
var GRADIENT_STOPS = [
  { offset: "0", color: "#16D2FF" },
  { offset: "0.27", color: "#1AA0FF" },
  { offset: "0.5", color: "#2B62FF" },
  { offset: "0.72", color: "#4326EE" },
  { offset: "0.9", color: "#7A18E8" },
  { offset: "1", color: "#B511E0" }
];
var GRADIENT_STOPS_BRIGHT = [
  { offset: "0", color: "#5fe3ff" },
  { offset: "0.27", color: "#46b6ff" },
  { offset: "0.5", color: "#5a86ff" },
  { offset: "0.72", color: "#7e5cff" },
  { offset: "0.9", color: "#ad5cff" },
  { offset: "1", color: "#d65cf0" }
];
var GRADIENT_GEOMETRY = { x1: "884", y1: "296", x2: "372", y2: "908" };
var BADGE_FIT_TRANSFORM = "translate(50 50) scale(0.09830) translate(-626.51 -596.63)";
var BADGE_VIEWBOX = "0 0 100 100";
function BrandLoader({
  variant = "swap",
  size = 40,
  label,
  className
}) {
  const reactId = useId();
  const gradientId = `syncanix-loader-grad-${reactId}`;
  const fill = `url(#${gradientId})`;
  const a11y = label ? { role: "status", "aria-live": "polite" } : { "aria-hidden": true };
  return /* @__PURE__ */ jsxs("div", { className: cn("syncanix-loader", className), "data-variant": variant, ...a11y, children: [
    /* @__PURE__ */ jsxs(
      "svg",
      {
        width: size,
        height: size,
        viewBox: MARK_VIEWBOX,
        xmlns: "http://www.w3.org/2000/svg",
        focusable: "false",
        "aria-hidden": "true",
        children: [
          /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx(
            "linearGradient",
            {
              id: gradientId,
              x1: GRADIENT_GEOMETRY.x1,
              y1: GRADIENT_GEOMETRY.y1,
              x2: GRADIENT_GEOMETRY.x2,
              y2: GRADIENT_GEOMETRY.y2,
              gradientUnits: "userSpaceOnUse",
              children: GRADIENT_STOPS.map((s) => /* @__PURE__ */ jsx("stop", { offset: s.offset, stopColor: s.color }, s.offset))
            }
          ) }),
          /* @__PURE__ */ jsx("path", { className: "syncanix-loader-blade-a", d: PATH_OUTER, fill }),
          /* @__PURE__ */ jsx("path", { className: "syncanix-loader-blade-b", d: PATH_INNER, fill })
        ]
      }
    ),
    label ? /* @__PURE__ */ jsx("span", { className: "syncanix-loader-sr-only", children: label }) : null
  ] });
}
var buttonVariants = cva(
  // Bedrock: 6px radius (the `controls` step), Geist's 520 control weight, and
  // ONE focus treatment — a 2px accent ring at 2px offset, identical on every
  // focusable thing in the system. Uniformity is the point: a focus ring that
  // varies per component is a focus ring people stop trusting.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-control transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-border disabled:bg-surface-well disabled:text-tertiary disabled:shadow-none",
  {
    variants: {
      variant: {
        // Uses the `action-*` interaction ladder rather than `opacity-90`, and
        // the reason is dark mode. Fading a filled button toward its page is
        // wrong in both schemes — it dims the LABEL along with the fill — but on
        // dark it is backwards: the button recedes into the background on hover,
        // when a hover should advance. The ladder already solves this: Daylight
        // hover is accent-600 (darker), Midnight hover is accent-ink (lighter),
        // each moving away from its own surface.
        // ACTIVE deliberately reuses the hover fill instead of `action-active`,
        // because that token is sized for a different pairing and drops the
        // button's own ink under the 4.5:1 floor — and WCAG does not exempt a
        // state for being transient. The press is expressed as a 2% scale
        // instead: a physical cue that costs no contrast and reads better than
        // a darker shade anyway. `transform-gpu` keeps it off the paint path;
        // `motion-reduce` drops it for anyone who asked for less motion.
        primary: "border border-primary bg-primary text-primary-foreground hover:border-action-hover hover:bg-action-hover active:bg-action-hover active:scale-[0.98] transform-gpu transition-[colors,transform] motion-reduce:active:scale-100",
        // The Bedrock "Preview" button: a real surface with a hairline edge and
        // e1, so it reads as a raised control against a well or a canvas rather
        // than as a hole punched in one. `border-hairline-strong` (not `border`)
        // because a secondary button that only whispers its edge is a ghost.
        secondary: "border border-hairline-strong bg-surface text-foreground shadow-elevation-1 hover:bg-surface-well active:bg-surface-well",
        // Quiet by default (`muted-foreground`) and rises to full ink on hover —
        // a ghost that starts at full ink is just a borderless secondary.
        // `disabled:` overrides the base flattening, which is written for the
        // variants that draw a box: losing their colour and keeping their shape
        // is exactly right for those, and exactly backwards here. A ghost draws
        // no box, so `disabled:border-border disabled:bg-surface-well` GAVE it
        // one — the inert control ending up more prominent than the working one.
        // Ghost is the app's whole row-action vocabulary, so it showed up
        // wherever a row action could be disabled. The ink still mutes (the base
        // `disabled:text-tertiary` stands): the shape is what must not change.
        ghost: "border border-transparent text-muted-foreground hover:bg-surface-well hover:text-foreground active:bg-surface-well disabled:border-transparent disabled:bg-transparent",
        // FILLED, and reserved for the confirm INSIDE a dialog — `.claude/rules/
        // ui-design.md` keeps the house destructive TRIGGER as `danger` below.
        destructive: "border border-destructive bg-destructive text-destructive-foreground hover:border-destructive-hover hover:bg-destructive-hover active:scale-[0.98] transform-gpu transition-[colors,transform] motion-reduce:active:scale-100",
        // The Bedrock "Revoke key" trigger: danger-toned but not yet loud — a
        // tint you can put in a toolbar next to ordinary buttons without the
        // page looking alarmed, which commits to red only under the cursor.
        danger: "border border-destructive/30 bg-destructive/[0.06] text-danger-foreground hover:border-destructive hover:bg-destructive hover:text-destructive-foreground"
      },
      size: {
        // `sm` is a deliberately compact size for dense UIs — NOT promised as a
        // touch target (see the JSDoc contract below), so it stays 28px.
        // The 5px radius is not an ad-hoc value: the spec tightens the corner
        // as the control shrinks (5 · 6 · 8 across small · medium · large), so
        // a 28px button at the 36px radius reads visibly rounder than its
        // neighbour. Measured off the spec's own rendered buttons.
        sm: "h-7 rounded-[5px] px-[11px] text-[12.5px]",
        // Bedrock's default control height is 36px — a step down from the old
        // 40px, which is what lets a toolbar of them sit inside a 56px bar and
        // still breathe. It grows to ≥44px only on a coarse (touch) pointer,
        // honoring the "44×44 touch target at md and up" contract without
        // bloating the desktop visual. Mirrors the widget's P5.5a treatment.
        md: "h-9 px-[15px] [@media(pointer:coarse)]:min-h-11",
        lg: "h-11 rounded-md px-5 text-[15px]",
        // Square icon buttons. Same three heights, no horizontal padding — a
        // 36px icon button that inherits `px-[15px]` is a 66px-wide lozenge,
        // which is how icon rows end up wider than the content they act on.
        "icon-sm": "h-7 w-7 p-0",
        icon: "h-9 w-9 p-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11",
        "icon-lg": "h-11 w-11 rounded-md p-0"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "md"
    }
  }
);
var Button = forwardRef(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        ref,
        type: asChild ? void 0 : type ?? "button",
        className: cn(buttonVariants({ variant, size }), className),
        ...props
      }
    );
  }
);
Button.displayName = "Button";
var calloutVariants = cva(
  "flex items-start gap-[11px] rounded-md border px-[15px] py-[13px] text-[13.5px] leading-[19px]",
  {
    variants: {
      tone: {
        info: "border-info/[0.18] bg-info/[0.05] text-muted-foreground",
        warning: "border-warning/25 bg-warning/[0.07] text-muted-foreground"
      }
    },
    defaultVariants: {
      tone: "info"
    }
  }
);
var calloutIconVariants = cva("mt-0.5 shrink-0", {
  variants: {
    tone: {
      info: "text-info-foreground",
      warning: "text-warning-foreground"
    }
  },
  defaultVariants: {
    tone: "info"
  }
});
var INFO_GLYPH = "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 4.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5ZM13.25 17h-2.5v-6.5h2.5V17Z";
var WARNING_GLYPH = "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1.25 4.5h2.5V13h-2.5V6.5ZM12 15a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5Z";
function Callout({
  title,
  body,
  children,
  tone,
  role = "status",
  dismissLabel,
  onDismiss,
  action,
  className,
  ...rest
}) {
  const showDismiss = onDismiss !== void 0 && dismissLabel !== void 0;
  return /* @__PURE__ */ jsxs("div", { role, className: cn(calloutVariants({ tone }), className), ...rest, children: [
    /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: calloutIconVariants({ tone }), children: /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", width: "20", height: "20", focusable: "false", children: /* @__PURE__ */ jsx("path", { d: tone === "warning" ? WARNING_GLYPH : INFO_GLYPH, fill: "currentColor" }) }) }),
    /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1 space-y-0.5", children: [
      /* @__PURE__ */ jsx("p", { className: "font-medium text-foreground", children: title }),
      /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: body }),
      children
    ] }),
    action !== void 0 && action !== null && action !== false ? /* @__PURE__ */ jsx("div", { className: "shrink-0 self-center", children: action }) : null,
    showDismiss ? /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "aria-label": dismissLabel,
        onClick: onDismiss,
        className: "ms-auto inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        children: /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", width: "18", height: "18", "aria-hidden": "true", focusable: "false", children: /* @__PURE__ */ jsx(
          "path",
          {
            d: "M6 6l12 12M18 6L6 18",
            stroke: "currentColor",
            strokeWidth: "2",
            strokeLinecap: "round",
            fill: "none"
          }
        ) })
      }
    ) : null
  ] });
}

// src/i18n/interpolate.ts
var PLACEHOLDER_PATTERN = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
function interpolate(template, params) {
  if (params === void 0) return template;
  return template.replace(PLACEHOLDER_PATTERN, (match, name) => {
    const value = params[name];
    return value === void 0 ? match : String(value);
  });
}

// src/i18n/context.ts
function defaultTranslator(key) {
  return key;
}
var FALLBACK_CONTEXT = {
  locale: "en",
  messages: {},
  t: defaultTranslator
};
var I18nContext = createContext(FALLBACK_CONTEXT);
function makeTranslator(messages) {
  return (key, params) => {
    const message = messages[key];
    if (message === void 0) return key;
    return interpolate(message, params);
  };
}
function I18nProvider(props) {
  const value = useMemo(
    () => ({
      locale: props.locale,
      messages: props.messages,
      t: makeTranslator(props.messages)
    }),
    [props.locale, props.messages]
  );
  return /* @__PURE__ */ jsx(I18nContext.Provider, { value, children: /* @__PURE__ */ jsx(DirectionProvider, { dir: getLocaleDirection(props.locale), children: props.children }) });
}
function useT() {
  return useContext(I18nContext).t;
}
function useLocale() {
  return useContext(I18nContext).locale;
}
function CapabilitySummaryCard({
  title,
  summary,
  icon,
  hint,
  configured = true,
  editLabel,
  onEdit,
  titleAs: TitleTag = "h2",
  disabled = false
}) {
  const t = useT();
  const label = editLabel ?? t("common.edit");
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "group relative rounded-lg border border-border bg-surface transition-colors",
        !disabled && "hover:border-primary/50 hover:bg-muted/40",
        "focus-within:border-ring focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring"
      ),
      children: /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3 p-4", children: [
        icon !== void 0 ? /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": "true",
            className: "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground",
            children: icon
          }
        ) : null,
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1 space-y-1", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
            /* @__PURE__ */ jsx(TitleTag, { className: "text-sm font-medium text-foreground", children: /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                disabled,
                onClick: onEdit,
                "aria-label": `${title}: ${summary}`,
                className: "text-start after:absolute after:inset-0 after:content-[''] focus-visible:outline-none disabled:cursor-not-allowed",
                children: title
              }
            ) }),
            !configured ? /* @__PURE__ */ jsx("span", { className: "rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground", children: t("capabilityCard.default") }) : null,
            hint !== void 0 ? /* @__PURE__ */ jsx("span", { className: "relative z-10", children: hint }) : null
          ] }),
          /* @__PURE__ */ jsx("p", { className: "text-xs leading-relaxed text-muted-foreground", children: summary })
        ] }),
        /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": "true",
            className: cn(
              "mt-1 shrink-0 text-xs font-medium text-muted-foreground transition-colors",
              !disabled && "group-hover:text-primary-strong"
            ),
            children: label
          }
        )
      ] })
    }
  );
}
var cardVariants = cva("rounded-lg border text-foreground", {
  variants: {
    padding: {
      none: "",
      sm: "p-3",
      md: "p-4",
      lg: "p-5"
    },
    /**
     * Bedrock principle 01 — "ruled, not floated". Shadow is reserved for what
     * genuinely floats ABOVE the page, and a dashboard tile does not.
     *
     * The ladder was rebased rather than renamed, and that is the substantive
     * change: `raised` used to mean `elevation-2`, the MENU step, so 34 in-page
     * tiles were drawing a dropdown's shadow. Four of them sat in a row across
     * the top of the Overview and made the KPI band read as four unrelated
     * objects that happened to be adjacent. It now means `elevation-1` — a 1px
     * resting lift in Daylight, a bare hairline ring in Midnight — which is
     * what "raised" should always have meant on a page.
     *
     * Rebasing the value beat editing 34 call sites: each of those call sites
     * was ASKING for "a resting card", and every one of them was right. Only
     * the answer was wrong.
     */
    elevation: {
      flat: "",
      raised: "shadow-elevation-1",
      // Genuinely floating layers only. If it does not overlay something, it
      // is `raised`.
      floating: "shadow-elevation-3"
    },
    /**
     * The card's ground. `surface` is the panel; `well` is Bedrock's inset —
     * secondary content recesses INTO its parent instead of stacking another
     * bordered card on top of it, which is how the system avoids box-in-box.
     * A `well` inside a `surface` is right; a `surface` inside a `surface` is
     * usually a `well` that has not been noticed yet.
     */
    tone: {
      surface: "border-hairline bg-surface",
      well: "border-hairline bg-surface-well"
    }
  },
  defaultVariants: {
    padding: "md",
    elevation: "flat",
    tone: "surface"
  }
});
var Card = forwardRef(
  ({ className, padding, elevation, tone, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(cardVariants({ padding, elevation, tone }), className),
      ...props
    }
  )
);
Card.displayName = "Card";
function SegmentBar({
  segments,
  hideLegend,
  formatShare,
  className
}) {
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  if (total <= 0) return null;
  return /* @__PURE__ */ jsxs("div", { className, children: [
    /* @__PURE__ */ jsx("div", { "aria-hidden": "true", className: "flex h-2.5 w-full overflow-hidden rounded-full bg-muted", children: segments.map(
      (s) => s.value > 0 ? /* @__PURE__ */ jsx(
        "span",
        {
          className: s.toneClassName,
          style: { inlineSize: `${String(s.value / total * 100)}%` }
        },
        s.key
      ) : null
    ) }),
    hideLegend !== true ? /* @__PURE__ */ jsx("ul", { className: "mt-2.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground", children: segments.map((s) => /* @__PURE__ */ jsxs("li", { className: "flex items-center gap-1.5", children: [
      /* @__PURE__ */ jsx(
        "span",
        {
          "aria-hidden": "true",
          className: cn("h-1.5 w-1.5 shrink-0 rounded-sm", s.toneClassName)
        }
      ),
      s.label,
      formatShare !== void 0 ? /* @__PURE__ */ jsx("span", { className: "tabular-nums opacity-70", children: formatShare(s.value / total) }) : null
    ] }, s.key)) }) : null
  ] });
}
var DEFAULT_SIZE = 76;
var DEFAULT_STROKE = 9;
function DonutGauge({
  fraction,
  toneClassName = "text-primary",
  size = DEFAULT_SIZE,
  strokeWidth = DEFAULT_STROKE,
  children,
  className
}) {
  const pct2 = Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = circumference * pct2;
  const center = size / 2;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn("relative inline-flex shrink-0 items-center justify-center", className),
      style: { inlineSize: size, blockSize: size },
      children: [
        /* @__PURE__ */ jsxs(
          "svg",
          {
            "aria-hidden": "true",
            width: size,
            height: size,
            viewBox: `0 0 ${String(size)} ${String(size)}`,
            className: "block",
            children: [
              /* @__PURE__ */ jsx(
                "circle",
                {
                  className: "text-hairline-strong",
                  cx: center,
                  cy: center,
                  r: radius,
                  fill: "none",
                  stroke: "currentColor",
                  strokeWidth
                }
              ),
              /* @__PURE__ */ jsx(
                "circle",
                {
                  className: toneClassName,
                  cx: center,
                  cy: center,
                  r: radius,
                  fill: "none",
                  stroke: "currentColor",
                  strokeWidth,
                  strokeLinecap: "round",
                  strokeDasharray: `${filled.toFixed(2)} ${(circumference - filled).toFixed(2)}`,
                  transform: `rotate(-90 ${String(center)} ${String(center)})`
                }
              )
            ]
          }
        ),
        children !== void 0 ? /* @__PURE__ */ jsx("span", { className: "absolute inset-0 flex items-center justify-center", children }) : null
      ]
    }
  );
}
function MiniBarChart({ data, formatValue }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-2.5", children: data.map((d) => {
    const width = Math.max(0, Math.min(100, d.value / max * 100));
    return /* @__PURE__ */ jsxs("li", { "data-testid": d.testId, className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsx("span", { className: "w-24 shrink-0 truncate text-xs text-muted-foreground", children: d.label }),
      /* @__PURE__ */ jsx(
        "span",
        {
          "aria-hidden": "true",
          className: "flex h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted",
          children: /* @__PURE__ */ jsx(
            "span",
            {
              className: cn("h-full rounded-full", d.toneClassName),
              style: { inlineSize: `${String(width)}%` }
            }
          )
        }
      ),
      /* @__PURE__ */ jsx("span", { className: "w-9 shrink-0 text-end font-mono text-xs font-semibold tabular-nums text-foreground", children: formatValue(d.value) })
    ] }, d.key);
  }) });
}
var VIEW_W = 100;
var VIEW_H = 30;
var PAD_Y = 2;
function Sparkline({ values, label }) {
  const locale = useLocale();
  const isRtl = getLocaleDirection(locale) === "rtl";
  const max = Math.max(1, ...values);
  const lastIndex = Math.max(1, values.length - 1);
  const points = values.map((value, index) => {
    const x = index / lastIndex * VIEW_W;
    const usable = VIEW_H - PAD_Y * 2;
    const y = VIEW_H - PAD_Y - value / max * usable;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  return /* @__PURE__ */ jsx(
    "svg",
    {
      className: "mt-2 block h-8 w-full overflow-visible text-primary",
      viewBox: `0 0 ${String(VIEW_W)} ${String(VIEW_H)}`,
      preserveAspectRatio: "none",
      role: "img",
      "aria-label": label,
      children: /* @__PURE__ */ jsx("g", { transform: isRtl ? `translate(${String(VIEW_W)},0) scale(-1,1)` : void 0, children: /* @__PURE__ */ jsx(
        "polyline",
        {
          points,
          fill: "none",
          stroke: "currentColor",
          strokeWidth: 2,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ) })
    }
  );
}
var ZERO_FLOOR_PERCENT = 2;
function ActivityStrip({
  data,
  describeDatum,
  onSelect,
  label,
  selectedKey
}) {
  if (data.length === 0) return null;
  const max = Math.max(...data.map((d) => d.value));
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "group",
      ...label === void 0 ? {} : { "aria-label": label },
      className: "flex h-16 items-end gap-px",
      children: data.map((datum) => {
        const fraction = max > 0 ? datum.value / max : 0;
        const percent = datum.value === 0 ? ZERO_FLOOR_PERCENT : Math.max(ZERO_FLOOR_PERCENT, Math.round(fraction * 1e4) / 100);
        const selected = selectedKey === datum.key;
        return /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-pressed": selected,
            "aria-label": describeDatum(datum.label, datum.value),
            onClick: () => {
              onSelect(datum.key);
            },
            className: "group flex h-full min-w-0 flex-1 items-end rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
            children: /* @__PURE__ */ jsx(
              "span",
              {
                "data-bar-fill": true,
                "aria-hidden": "true",
                style: { blockSize: `${String(percent)}%` },
                className: cn(
                  "w-full rounded-sm transition-colors",
                  selected ? "bg-primary" : "bg-primary/35 group-hover:bg-primary/60",
                  // A zero column is muted rather than primary-tinted: it is a baseline,
                  // not a small amount of activity.
                  datum.value === 0 && "bg-border group-hover:bg-muted-foreground/40"
                )
              }
            )
          },
          datum.key
        );
      })
    }
  );
}
var Checkbox = forwardRef(
  ({ className, indeterminate = false, onCheckedChange, disabled, ...props }, forwardedRef) => {
    const innerRef = useRef(null);
    const setRefs = useCallback(
      (node) => {
        innerRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef !== null) forwardedRef.current = node;
      },
      [forwardedRef]
    );
    useEffect(() => {
      if (innerRef.current !== null) innerRef.current.indeterminate = indeterminate;
    }, [indeterminate, props.checked]);
    const handleChange = (event) => {
      onCheckedChange?.(event.target.checked);
    };
    return /* @__PURE__ */ jsxs("span", { className: "relative inline-flex shrink-0 items-center justify-center", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          ref: setRefs,
          type: "checkbox",
          disabled,
          "aria-checked": indeterminate ? "mixed" : void 0,
          onChange: handleChange,
          className: cn(
            // Bedrock: 18px on the 3px chip radius with a 1.5px hairline edge
            // — a 2px edge at this size eats a third of the box and reads as a
            // filled square rather than an empty one.
            "peer size-[18px] shrink-0 cursor-pointer appearance-none rounded-[3px] border-[1.5px] border-hairline-strong bg-surface transition-colors",
            "checked:border-primary checked:bg-primary indeterminate:border-primary indeterminate:bg-primary",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          ),
          ...props
        }
      ),
      /* @__PURE__ */ jsx(
        "svg",
        {
          "aria-hidden": "true",
          viewBox: "0 0 24 24",
          className: "pointer-events-none absolute hidden size-3 text-primary-foreground peer-checked:block peer-indeterminate:hidden",
          fill: "none",
          children: /* @__PURE__ */ jsx(
            "path",
            {
              d: "M5 12.5l4 4L19 7",
              stroke: "currentColor",
              strokeWidth: "3",
              strokeLinecap: "round",
              strokeLinejoin: "round"
            }
          )
        }
      ),
      /* @__PURE__ */ jsx(
        "svg",
        {
          "aria-hidden": "true",
          viewBox: "0 0 24 24",
          className: "pointer-events-none absolute hidden size-3 text-primary-foreground peer-indeterminate:block",
          fill: "none",
          children: /* @__PURE__ */ jsx("path", { d: "M6 12h12", stroke: "currentColor", strokeWidth: "3", strokeLinecap: "round" })
        }
      )
    ] });
  }
);
Checkbox.displayName = "Checkbox";
function EyeDropperButton({ onPick }) {
  const t = useT();
  const Ctor = globalThis.EyeDropper;
  if (Ctor === void 0) {
    return null;
  }
  async function pick() {
    if (Ctor === void 0) {
      return;
    }
    try {
      const { sRGBHex } = await new Ctor().open();
      onPick(sRGBHex);
    } catch {
    }
  }
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      "aria-label": t("colorpicker.eyedropper"),
      onClick: () => {
        void pick();
      },
      className: "mt-5 grid size-9 shrink-0 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
      children: /* @__PURE__ */ jsx(
        "svg",
        {
          "aria-hidden": "true",
          viewBox: "0 0 16 16",
          className: "size-4",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "1.5",
          children: /* @__PURE__ */ jsx(
            "path",
            {
              d: "M10.5 2.5a1.6 1.6 0 0 1 2.3 2.3l-1 1 1 1-1.2 1.2-1-1-4.4 4.4-2.4.6.6-2.4 4.4-4.4-1-1L9.5 2.5z",
              strokeLinejoin: "round"
            }
          )
        }
      )
    }
  );
}
function positionToSv(clientX, clientY, rect) {
  const width = rect.width || 1;
  const height = rect.height || 1;
  const x = clamp((clientX - rect.left) / width, 0, 1);
  const y = clamp((clientY - rect.top) / height, 0, 1);
  return { s: Math.round(x * 100), v: Math.round((1 - y) * 100) };
}
function svToPosition(sv) {
  return { xPct: clamp(sv.s, 0, 100), yPct: clamp(100 - sv.v, 0, 100) };
}
function NumberField({ label, symbol, value, max, onChange }) {
  return /* @__PURE__ */ jsxs("label", { className: "flex flex-1 flex-col items-center gap-1", children: [
    /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "text-xs font-medium text-muted-foreground", children: symbol }),
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "number",
        inputMode: "numeric",
        "aria-label": label,
        min: 0,
        max,
        value,
        onChange: (e) => {
          const parsed = Number(e.target.value);
          onChange(Number.isNaN(parsed) ? 0 : clamp(Math.round(parsed), 0, max));
        },
        className: "h-8 w-full rounded-md border border-border bg-background px-1 text-center font-mono text-xs text-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      }
    )
  ] });
}
function HslInputs({
  hsl,
  onChange,
  opacity,
  onOpacityChange
}) {
  const t = useT();
  return /* @__PURE__ */ jsxs("div", { className: "flex items-end gap-2", children: [
    /* @__PURE__ */ jsx(
      NumberField,
      {
        label: t("colorpicker.hue"),
        symbol: "H",
        value: hsl.h,
        max: 360,
        onChange: (h) => {
          onChange({ ...hsl, h });
        }
      }
    ),
    /* @__PURE__ */ jsx(
      NumberField,
      {
        label: t("colorpicker.saturation"),
        symbol: "S",
        value: hsl.s,
        max: 100,
        onChange: (s) => {
          onChange({ ...hsl, s });
        }
      }
    ),
    /* @__PURE__ */ jsx(
      NumberField,
      {
        label: t("colorpicker.lightness"),
        symbol: "L",
        value: hsl.l,
        max: 100,
        onChange: (l) => {
          onChange({ ...hsl, l });
        }
      }
    ),
    opacity !== void 0 && onOpacityChange !== void 0 ? /* @__PURE__ */ jsx(
      NumberField,
      {
        label: t("colorpicker.opacity"),
        symbol: "A",
        value: Math.round(opacity * 100),
        max: 100,
        onChange: (a) => {
          onOpacityChange(clamp(a / 100, 0, 1));
        }
      }
    ) : null
  ] });
}
var HUE_GRADIENT = "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)";
var CHECKERBOARD = "repeating-conic-gradient(#c8c8c8 0% 25%, #ffffff 0% 50%) 50% / 12px 12px";
function ColorPicker({
  value,
  onChange,
  alpha = false,
  className
}) {
  const t = useT();
  const hexId = useId();
  const svRef = useRef(null);
  const [hsv, setHsv] = useState(() => hexToHsv(value) ?? { h: 0, s: 0, v: 0 });
  const [opacity, setOpacity] = useState(() => parseHex(value)?.alpha ?? 1);
  const [hexText, setHexText] = useState(() => value);
  function outHex(nextHsv, nextOpacity) {
    return hsvToHex(nextHsv, alpha ? nextOpacity : void 0);
  }
  const hsvRef = useRef(hsv);
  hsvRef.current = hsv;
  const opacityRef = useRef(opacity);
  opacityRef.current = opacity;
  useEffect(() => {
    const parsed = parseHex(value);
    if (parsed === null) {
      return;
    }
    const normalized = formatHex(parsed.rgb, alpha ? parsed.alpha : void 0);
    if (normalized !== outHex(hsvRef.current, opacityRef.current)) {
      setHsv(rgbToHsv(parsed.rgb));
      if (alpha) {
        setOpacity(parsed.alpha);
      }
      setHexText(normalized);
    }
  }, [value]);
  function emit(nextHsv, nextOpacity) {
    const hex = outHex(nextHsv, nextOpacity);
    setHsv(nextHsv);
    setOpacity(nextOpacity);
    setHexText(hex);
    onChange(hex);
  }
  function onHexInput(text) {
    setHexText(text);
    const parsed = parseHex(text);
    if (parsed !== null) {
      setHsv(rgbToHsv(parsed.rgb));
      if (alpha) {
        setOpacity(parsed.alpha);
      }
      onChange(formatHex(parsed.rgb, alpha ? parsed.alpha : void 0));
    }
  }
  function onHslChange(hsl) {
    emit(hslToHsv(hsl), opacity);
  }
  function pointerToColour(event) {
    const rect = svRef.current?.getBoundingClientRect();
    if (rect === void 0) {
      return;
    }
    emit({ h: hsv.h, ...positionToSv(event.clientX, event.clientY, rect) }, opacity);
  }
  function onSvKeyDown(event) {
    let { s, v } = hsv;
    switch (event.key) {
      case "ArrowLeft":
        s = clamp(s - 1, 0, 100);
        break;
      case "ArrowRight":
        s = clamp(s + 1, 0, 100);
        break;
      case "ArrowUp":
        v = clamp(v + 1, 0, 100);
        break;
      case "ArrowDown":
        v = clamp(v - 1, 0, 100);
        break;
      default:
        return;
    }
    event.preventDefault();
    emit({ h: hsv.h, s, v }, opacity);
  }
  function applyPicked(sRGBHex) {
    const parsed = parseHex(sRGBHex);
    if (parsed !== null) {
      const hex = formatHex(parsed.rgb, alpha ? opacity : void 0);
      setHsv(rgbToHsv(parsed.rgb));
      setHexText(hex);
      onChange(hex);
    }
  }
  const { xPct, yPct } = svToPosition(hsv);
  const hueHex = hsvToHex({ h: hsv.h, s: 100, v: 100 });
  const swatch = outHex(hsv, opacity);
  const opaque = hsvToHex(hsv);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex w-60 flex-col gap-3", className), children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: svRef,
        role: "slider",
        tabIndex: 0,
        "aria-label": t("colorpicker.saturationValue"),
        "aria-valuemin": 0,
        "aria-valuemax": 100,
        "aria-valuenow": hsv.s,
        "aria-valuetext": `S ${hsv.s}%, V ${hsv.v}%`,
        onPointerDown: (e) => {
          svRef.current?.setPointerCapture(e.pointerId);
          pointerToColour(e);
        },
        onPointerMove: (e) => {
          if (e.buttons > 0) {
            pointerToColour(e);
          }
        },
        onKeyDown: onSvKeyDown,
        className: "relative h-40 w-full cursor-crosshair rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        style: {
          backgroundColor: hueHex,
          backgroundImage: "linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)"
        },
        children: /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": "true",
            className: "pointer-events-none absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow",
            style: { left: `${xPct}%`, top: `${yPct}%`, backgroundColor: opaque }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "range",
        "aria-label": t("colorpicker.hue"),
        min: 0,
        max: 360,
        step: 1,
        value: hsv.h,
        onChange: (e) => {
          emit({ ...hsv, h: Number(e.target.value) }, opacity);
        },
        className: "h-3 w-full cursor-pointer appearance-none rounded-full",
        style: { background: HUE_GRADIENT }
      }
    ),
    alpha ? /* @__PURE__ */ jsx(
      "input",
      {
        type: "range",
        "aria-label": t("colorpicker.opacity"),
        min: 0,
        max: 100,
        step: 1,
        value: Math.round(opacity * 100),
        onChange: (e) => {
          emit(hsv, clamp(Number(e.target.value) / 100, 0, 1));
        },
        className: "h-3 w-full cursor-pointer appearance-none rounded-full",
        style: {
          backgroundImage: `linear-gradient(to right, transparent, ${opaque}), ${CHECKERBOARD}`
        }
      }
    ) : null,
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx(
        "span",
        {
          "aria-hidden": "true",
          className: "size-8 shrink-0 rounded-md border border-hairline",
          style: { backgroundColor: swatch, backgroundImage: alpha ? CHECKERBOARD : void 0 }
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-1 flex-col gap-1", children: [
        /* @__PURE__ */ jsx("label", { htmlFor: hexId, className: "text-xs font-medium text-muted-foreground", children: t("colorpicker.hexValue") }),
        /* @__PURE__ */ jsx(
          "input",
          {
            id: hexId,
            value: hexText,
            spellCheck: false,
            autoComplete: "off",
            onChange: (e) => {
              onHexInput(e.target.value);
            },
            className: "h-9 w-full rounded-md border border-border bg-background px-2 font-mono text-sm text-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          }
        )
      ] }),
      /* @__PURE__ */ jsx(EyeDropperButton, { onPick: applyPicked })
    ] }),
    /* @__PURE__ */ jsx(
      HslInputs,
      {
        hsl: hsvToHsl(hsv),
        onChange: onHslChange,
        opacity: alpha ? opacity : void 0,
        onOpacityChange: alpha ? (next) => {
          emit(hsv, next);
        } : void 0
      }
    )
  ] });
}
function ApprovalQueue({
  items,
  approveLabel,
  rejectLabel,
  onApprove,
  onReject,
  onEdit,
  editLabel,
  canDecide,
  permissionHint,
  busyId,
  emptyLabel,
  banner,
  className
}) {
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-4", className), children: [
    banner,
    !canDecide && items.length > 0 && permissionHint !== void 0 ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: permissionHint }) : null,
    items.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: emptyLabel }) : /* @__PURE__ */ jsx("ul", { className: "space-y-4", children: items.map((item) => {
      const busy = busyId === item.id;
      const titleId = `approval-queue-title-${item.id}`;
      return (
        // Name the card by its title so a screen reader announces WHICH
        // proposal, not a generic list item (WCAG 1.3.1 / 2.4.6).
        /* @__PURE__ */ jsxs(
          "li",
          {
            "aria-labelledby": titleId,
            className: "space-y-3 rounded-xl border border-hairline bg-surface p-4",
            children: [
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("p", { id: titleId, className: "text-sm font-semibold text-foreground", children: item.title }),
                item.meta !== void 0 ? /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-xs text-muted-foreground", children: item.meta }) : null
              ] }),
              item.body,
              /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-end gap-2", children: [
                onEdit !== void 0 && editLabel !== void 0 && item.editable !== false ? /* @__PURE__ */ jsx(
                  Button,
                  {
                    type: "button",
                    variant: "secondary",
                    size: "md",
                    disabled: !canDecide || busy,
                    "aria-busy": busy,
                    "aria-label": `${editLabel}: ${item.title}`,
                    onClick: () => {
                      onEdit(item.id);
                    },
                    children: editLabel
                  }
                ) : null,
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    type: "button",
                    variant: "ghost",
                    size: "md",
                    disabled: !canDecide || busy,
                    "aria-busy": busy,
                    "aria-label": `${rejectLabel}: ${item.title}`,
                    onClick: () => {
                      onReject(item.id);
                    },
                    children: rejectLabel
                  }
                ),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    type: "button",
                    variant: "primary",
                    size: "md",
                    disabled: !canDecide || busy,
                    "aria-busy": busy,
                    "aria-label": `${approveLabel}: ${item.title}`,
                    onClick: () => {
                      onApprove(item.id);
                    },
                    children: approveLabel
                  }
                )
              ] })
            ]
          },
          item.id
        )
      );
    }) })
  ] });
}
function DiffCard({
  rows,
  beforeLabel,
  afterLabel,
  emptyLabel,
  className
}) {
  if (rows.length === 0) {
    return /* @__PURE__ */ jsx("p", { className: cn("text-sm text-muted-foreground", className), children: emptyLabel });
  }
  return /* @__PURE__ */ jsx("ul", { className: cn("space-y-4", className), children: rows.map((row) => /* @__PURE__ */ jsxs("li", { className: "space-y-2", children: [
    /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-foreground", children: row.label }),
    /* @__PURE__ */ jsxs("div", { className: "grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-stretch", children: [
      /* @__PURE__ */ jsxs("div", { className: "rounded-lg border border-hairline bg-muted/40 p-2.5", children: [
        /* @__PURE__ */ jsx("p", { className: "mb-1 text-xs text-muted-foreground", children: beforeLabel }),
        /* @__PURE__ */ jsx("p", { className: "whitespace-pre-wrap break-words text-sm text-muted-foreground", children: row.before })
      ] }),
      /* @__PURE__ */ jsx(
        "div",
        {
          "aria-hidden": "true",
          className: "hidden items-center justify-center text-muted-foreground sm:flex",
          children: /* @__PURE__ */ jsx(
            "svg",
            {
              viewBox: "0 0 24 24",
              width: "16",
              height: "16",
              fill: "none",
              className: "rtl:rotate-180",
              children: /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M4 12h15m0 0-5.5-5.5M19 12l-5.5 5.5",
                  stroke: "currentColor",
                  strokeWidth: "1.8",
                  strokeLinecap: "round",
                  strokeLinejoin: "round"
                }
              )
            }
          )
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "rounded-lg border border-primary/30 bg-primary/5 p-2.5", children: [
        /* @__PURE__ */ jsx("p", { className: "mb-1 text-xs text-muted-foreground", children: afterLabel }),
        /* @__PURE__ */ jsx("p", { className: "whitespace-pre-wrap break-words text-sm font-medium text-foreground", children: row.after })
      ] })
    ] })
  ] }, row.key)) });
}
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogClose = DialogPrimitive.Close;
var DialogOverlay = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn("fixed inset-0 z-50 bg-overlay backdrop-blur-sm", className),
    ...props
  }
));
DialogOverlay.displayName = "DialogOverlay";
function CloseIcon() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 16 16",
      className: "size-4",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      children: /* @__PURE__ */ jsx("path", { d: "m4 4 8 8M12 4l-8 8", strokeLinecap: "round" })
    }
  );
}
function useOpener() {
  const [opener] = useState(
    () => typeof document === "undefined" ? null : document.activeElement
  );
  return opener;
}
var DialogContent = forwardRef(({ className, children, closeDisabled = false, onCloseAutoFocus, ...props }, ref) => {
  const t = useT();
  const opener = useOpener();
  return /* @__PURE__ */ jsxs(DialogPrimitive.Portal, { children: [
    /* @__PURE__ */ jsx(DialogOverlay, {}),
    /* @__PURE__ */ jsxs(
      DialogPrimitive.Content,
      {
        ref,
        onCloseAutoFocus: (event) => {
          onCloseAutoFocus?.(event);
          if (event.defaultPrevented) return;
          if (!(opener instanceof HTMLElement) || !opener.isConnected) return;
          event.preventDefault();
          opener.focus();
        },
        className: cn(
          "fixed start-1/2 top-1/2 z-50 grid max-h-[calc(100dvh-2rem)] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-xl bg-surface p-[22px] shadow-elevation-3 outline-none rtl:translate-x-1/2",
          className
        ),
        ...props,
        children: [
          children,
          /* @__PURE__ */ jsxs(
            DialogPrimitive.Close,
            {
              disabled: closeDisabled,
              className: "absolute end-4 top-4 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50",
              children: [
                /* @__PURE__ */ jsx(CloseIcon, {}),
                /* @__PURE__ */ jsx("span", { className: "sr-only", children: t("common.close") })
              ]
            }
          )
        ]
      }
    )
  ] });
});
DialogContent.displayName = "DialogContent";
function DialogHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col gap-1.5 text-start", className), ...props });
}
function DialogFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className),
      ...props
    }
  );
}
var DialogTitle = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn("text-h2 text-foreground", className),
    ...props
  }
));
DialogTitle.displayName = "DialogTitle";
var DialogDescription = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DialogDescription.displayName = "DialogDescription";
function DrawerHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "flex flex-none flex-col gap-1.5 border-b border-hairline p-5 pe-12 text-start",
        className
      ),
      ...props
    }
  );
}
function DrawerBody({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex-1 overflow-y-auto p-5", className), ...props });
}
function DrawerFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "flex flex-none items-center justify-end gap-2 border-t border-hairline bg-surface-well p-4",
        className
      ),
      ...props
    }
  );
}
var DrawerTitle = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn("text-h2 text-foreground", className),
    ...props
  }
));
DrawerTitle.displayName = "DrawerTitle";
var DrawerDescription = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DrawerDescription.displayName = "DrawerDescription";
var DrawerOpenerContext = createContext(null);
function Drawer({
  open,
  onOpenChange,
  dirty = false,
  onGuardedClose,
  children
}) {
  const openerRef = useRef(null);
  const wasOpenRef = useRef(false);
  if (open && !wasOpenRef.current) {
    const active = typeof document === "undefined" ? null : document.activeElement;
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }
  wasOpenRef.current = open;
  return /* @__PURE__ */ jsx(DrawerOpenerContext.Provider, { value: openerRef, children: /* @__PURE__ */ jsx(
    DialogPrimitive.Root,
    {
      open,
      onOpenChange: (next) => {
        if (!next && dirty) {
          onGuardedClose?.();
          return;
        }
        onOpenChange(next);
      },
      children
    }
  ) });
}
var DrawerTrigger = DialogPrimitive.Trigger;
var DrawerClose = DialogPrimitive.Close;
var DrawerOverlay = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn("fixed inset-0 z-50 bg-overlay backdrop-blur-sm", className),
    ...props
  }
));
DrawerOverlay.displayName = "DrawerOverlay";
function CloseIcon2() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 16 16",
      className: "size-4",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      children: /* @__PURE__ */ jsx("path", { d: "m4 4 8 8M12 4l-8 8", strokeLinecap: "round" })
    }
  );
}
var SIZE_CLASS = {
  sm: "sm:max-w-md",
  md: "sm:max-w-xl",
  lg: "sm:max-w-2xl"
};
var DrawerContent = forwardRef(({ className, children, size = "md", onCloseAutoFocus, ...props }, ref) => {
  const t = useT();
  const openerRef = useContext(DrawerOpenerContext);
  return /* @__PURE__ */ jsxs(DialogPrimitive.Portal, { children: [
    /* @__PURE__ */ jsx(DrawerOverlay, {}),
    /* @__PURE__ */ jsxs(
      DialogPrimitive.Content,
      {
        ref,
        className: cn(
          // Full-height, pinned to the inline-end edge with logical insets so it
          // mirrors under RTL; a rounded inline-start corner + start border. Full
          // width on mobile (a sheet), capped by `size` from `sm` up.
          "fixed inset-y-0 end-0 z-50 flex h-full w-full flex-col gap-0 bg-surface p-0 shadow-elevation-3 outline-none rounded-s-xl",
          SIZE_CLASS[size],
          className
        ),
        onCloseAutoFocus: (event) => {
          onCloseAutoFocus?.(event);
          if (event.defaultPrevented) return;
          const opener = openerRef?.current;
          if (opener?.isConnected !== true) return;
          event.preventDefault();
          opener.focus();
        },
        ...props,
        children: [
          children,
          /* @__PURE__ */ jsxs(DialogPrimitive.Close, { className: "absolute end-4 top-4 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring", children: [
            /* @__PURE__ */ jsx(CloseIcon2, {}),
            /* @__PURE__ */ jsx("span", { className: "sr-only", children: t("common.close") })
          ] })
        ]
      }
    )
  ] });
});
DrawerContent.displayName = "DrawerContent";
function CheckIcon() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 16 16",
      className: "size-4",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.75",
      children: /* @__PURE__ */ jsx("path", { d: "m3.5 8.5 3 3 6-7", strokeLinecap: "round", strokeLinejoin: "round" })
    }
  );
}
var ROW_CLASS = "flex min-h-[34px] cursor-pointer select-none items-center gap-[9px] rounded-[5px] px-[10px] py-[7px] text-[13.5px] outline-none transition-colors data-[highlighted]:bg-surface-well data-[disabled]:pointer-events-none data-[disabled]:text-tertiary [@media(pointer:coarse)]:min-h-11";
var ROW_DESTRUCTIVE = "text-danger-foreground data-[highlighted]:bg-destructive/10 data-[highlighted]:text-danger-foreground";
function DropdownMenu({
  trigger,
  items,
  align = "end",
  modal = true,
  className,
  selectedId,
  open,
  onOpenChange
}) {
  const picker = selectedId !== void 0;
  const rows = items.map((item) => {
    const shared = {
      disabled: item.disabled ?? false,
      onSelect: () => {
        item.onSelect();
      },
      className: cn(ROW_CLASS, item.destructive === true && ROW_DESTRUCTIVE)
    };
    return picker ? /* @__PURE__ */ jsxs(DropdownMenuPrimitive.RadioItem, { value: item.id, ...shared, children: [
      item.icon,
      item.label,
      /* @__PURE__ */ jsx("span", { className: "ms-auto flex items-center justify-center ps-4 text-primary-strong", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon, {}) }) })
    ] }, item.id) : /* @__PURE__ */ jsxs(DropdownMenuPrimitive.Item, { ...shared, children: [
      item.icon,
      item.label
    ] }, item.id);
  });
  return /* @__PURE__ */ jsxs(
    DropdownMenuPrimitive.Root,
    {
      modal,
      ...open !== void 0 ? { open } : {},
      ...onOpenChange !== void 0 ? { onOpenChange } : {},
      children: [
        /* @__PURE__ */ jsx(DropdownMenuPrimitive.Trigger, { asChild: true, children: trigger }),
        /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(
          DropdownMenuPrimitive.Content,
          {
            align,
            sideOffset: 6,
            className: cn(
              "z-50 min-w-[10rem] overflow-hidden rounded-md bg-surface p-[5px] text-foreground shadow-elevation-2",
              className
            ),
            children: picker ? (
              // `value` alone drives the checked row; each item's own onSelect
              // stays the single source of the change (no duplicate handler).
              /* @__PURE__ */ jsx(DropdownMenuPrimitive.RadioGroup, { value: selectedId, children: rows })
            ) : rows
          }
        ) })
      ]
    }
  );
}
function EmptyState({
  title,
  description,
  action,
  icon,
  className,
  ...rest
}) {
  const guided = description !== void 0;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "text-center",
        guided ? (
          // The guided state is a PANEL, not a placeholder. `border-dashed`
          // is the drop-target idiom — it says "something belongs here and is
          // missing", which is the wrong sentence for a surface whose whole
          // job is to tell a first-time user what to do next. A solid hairline
          // over a faint tint reads as a considered region instead.
          "rounded-lg border border-hairline bg-surface-well p-10"
        ) : (
          // The quiet state stays exactly as it was: a filter that matched
          // nothing should not become an event.
          "rounded-lg border border-dashed border-hairline-strong p-8"
        ),
        className
      ),
      ...rest,
      children: [
        guided && icon !== void 0 ? /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": "true",
            className: "mx-auto mb-4 flex size-11 items-center justify-center rounded-full bg-primary/10 text-primary-strong",
            children: icon
          }
        ) : null,
        /* @__PURE__ */ jsx("p", { className: guided ? "text-h3 text-foreground" : "text-sm text-muted-foreground", children: title }),
        description !== void 0 ? (
          // A measure, not the full container width. Centred text set edge to edge
          // is unreadable at panel widths, and `ui-design.md` § 7 asks for width to
          // be judged in the LONGEST locale — German and Arabic run well past the
          // English line this was drafted against.
          /* @__PURE__ */ jsx("p", { className: "mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground", children: description })
        ) : null,
        action !== void 0 ? /* @__PURE__ */ jsx("div", { className: cn("flex flex-wrap justify-center gap-2", guided ? "mt-5" : "mt-4"), children: action }) : null
      ]
    }
  );
}
function ErrorState({
  title,
  description,
  action,
  className,
  headingLevel = 1,
  ...rest
}) {
  const Heading = headingLevel === 2 ? "h2" : "h1";
  return /* @__PURE__ */ jsx(
    "section",
    {
      className: cn("flex min-h-[60vh] items-center justify-center p-6", className),
      ...rest,
      children: /* @__PURE__ */ jsxs("div", { className: "max-w-md space-y-4 text-center", children: [
        /* @__PURE__ */ jsx(Heading, { className: "text-h1 text-foreground", children: title }),
        /* @__PURE__ */ jsx("p", { role: "alert", className: "text-sm text-muted-foreground", children: description }),
        action
      ] })
    }
  );
}
function DownloadIcon() {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 16 16",
      className: "size-4",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      children: [
        /* @__PURE__ */ jsx("path", { d: "M8 2v8m0 0 3-3m-3 3L5 7", strokeLinecap: "round", strokeLinejoin: "round" }),
        /* @__PURE__ */ jsx("path", { d: "M2.5 11.5v1a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-1", strokeLinecap: "round" })
      ]
    }
  );
}
function ChevronIcon2() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 16 16",
      className: "size-3 opacity-60",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      children: /* @__PURE__ */ jsx("path", { d: "m4 6 4 4 4-4", strokeLinecap: "round", strokeLinejoin: "round" })
    }
  );
}
function ExportMenu({
  formats,
  onExport,
  disabled = false,
  label,
  ariaLabel,
  align = "end",
  className
}) {
  const t = useT();
  const triggerLabel = label ?? t("ui.export.trigger");
  return /* @__PURE__ */ jsxs(DropdownMenuPrimitive.Root, { children: [
    /* @__PURE__ */ jsx(DropdownMenuPrimitive.Trigger, { asChild: true, disabled, children: /* @__PURE__ */ jsxs(
      Button,
      {
        type: "button",
        size: "sm",
        variant: "secondary",
        disabled,
        "aria-label": ariaLabel ?? triggerLabel,
        className,
        children: [
          /* @__PURE__ */ jsx(DownloadIcon, {}),
          triggerLabel,
          /* @__PURE__ */ jsx(ChevronIcon2, {})
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(
      DropdownMenuPrimitive.Content,
      {
        align,
        sideOffset: 6,
        className: cn(
          "z-50 min-w-[10rem] overflow-hidden rounded-md border border-hairline bg-surface p-1 text-foreground shadow-elevation-3"
        ),
        children: formats.map((format) => /* @__PURE__ */ jsx(
          DropdownMenuPrimitive.Item,
          {
            onSelect: () => {
              onExport(format);
            },
            className: cn(
              "flex min-h-[40px] cursor-pointer select-none items-center gap-2 rounded-sm px-2 text-sm outline-none data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
            ),
            children: t(`ui.export.format.${format}`)
          },
          format
        ))
      }
    ) })
  ] });
}

// src/components/FilterBar/filter-state.ts
function appliedDimensions(dimensions, state) {
  return Object.keys(state).map((id) => dimensions.find((d) => d.id === id)).filter((d) => d !== void 0);
}
function availableDimensions(dimensions, state) {
  return dimensions.filter((d) => !(d.id in state));
}
function searchDimensions(dimensions, query) {
  const q = normalize(query);
  if (q === "") return [...dimensions];
  return dimensions.filter(
    (d) => normalize(d.label).includes(q) || normalize(d.group ?? "").includes(q)
  );
}
function normalize(value) {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").trim().toLowerCase();
}
function addDimension(state, id) {
  if (id in state) return state;
  return { ...state, [id]: [] };
}
function removeDimension(state, id) {
  if (!(id in state)) return state;
  return Object.fromEntries(Object.entries(state).filter(([key]) => key !== id));
}
function toggleValue(state, dimension, value) {
  const current = state[dimension.id] ?? [];
  if (dimension.mode === "single") {
    const next2 = current[0] === value ? [] : [value];
    return { ...state, [dimension.id]: next2 };
  }
  const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
  return { ...state, [dimension.id]: next };
}
function clearAll() {
  return {};
}
function appliedCount(state) {
  return Object.keys(state).length;
}
function summarizeValues(dimension, state) {
  const values = state[dimension.id] ?? [];
  if (values.length === 0) return null;
  return values.map((v) => dimension.options.find((o) => o.value === v)?.label ?? v).join(", ");
}
function matchesFilters(row, dimensions, state, selectorFor) {
  for (const [id, values] of Object.entries(state)) {
    if (values.length === 0) continue;
    const dimension = dimensions.find((d) => d.id === id);
    if (dimension === void 0) continue;
    const rowValues = selectorFor(dimension, row);
    if (!values.some((v) => rowValues.includes(v))) return false;
  }
  return true;
}
function FilterDimensionPicker({
  dimensions,
  onAdd
}) {
  const t = useT();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listId = useRef(`filterbar-list-${Math.random().toString(36).slice(2, 8)}`).current;
  const matches = useMemo(() => searchDimensions(dimensions, query), [dimensions, query]);
  const activeIndex = matches.length === 0 ? -1 : Math.min(active, matches.length - 1);
  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(
        (i) => matches.length === 0 ? 0 : (Math.min(i, matches.length - 1) + 1) % matches.length
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(
        (i) => matches.length === 0 ? 0 : (Math.min(i, matches.length - 1) + matches.length - 1) % matches.length
      );
      return;
    }
    if (event.key === "Enter") {
      const target = matches[activeIndex];
      if (target !== void 0) {
        event.preventDefault();
        onAdd(target);
      }
    }
  };
  let lastGroup;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "search",
        role: "combobox",
        "aria-expanded": "true",
        "aria-controls": listId,
        "aria-autocomplete": "list",
        "aria-activedescendant": activeIndex >= 0 ? `${listId}-${String(activeIndex)}` : void 0,
        value: query,
        autoComplete: "off",
        onChange: (e) => {
          setQuery(e.target.value);
          setActive(0);
        },
        onKeyDown,
        "aria-label": t("ui.filterbar.search.label"),
        placeholder: t("ui.filterbar.search.placeholder"),
        className: "w-full rounded-sm border border-hairline bg-muted px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      }
    ),
    matches.length === 0 ? /* @__PURE__ */ jsx("p", { role: "status", className: "px-2 py-3 text-sm text-muted-foreground", children: dimensions.length === 0 ? t("ui.filterbar.allApplied") : t("ui.filterbar.noMatch", { query }) }) : /* @__PURE__ */ jsx(
      "ul",
      {
        id: listId,
        role: "listbox",
        "aria-label": t("ui.filterbar.listLabel"),
        className: "max-h-72 overflow-y-auto",
        children: matches.map((dimension, index) => {
          const heading = dimension.group !== lastGroup ? dimension.group : void 0;
          lastGroup = dimension.group;
          return /* @__PURE__ */ jsxs("li", { children: [
            heading !== void 0 ? /* @__PURE__ */ jsx(
              "p",
              {
                "aria-hidden": "true",
                className: "px-2 pb-1 pt-2.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground",
                children: heading
              }
            ) : null,
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                id: `${listId}-${String(index)}`,
                role: "option",
                "aria-selected": index === activeIndex,
                onClick: () => {
                  onAdd(dimension);
                },
                onMouseEnter: () => {
                  setActive(index);
                },
                className: cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-start text-sm text-foreground",
                  index === activeIndex && "bg-primary-subtle"
                ),
                children: /* @__PURE__ */ jsx("span", { className: "truncate", children: dimension.label })
              }
            )
          ] }, dimension.id);
        })
      }
    )
  ] });
}
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverAnchor = PopoverPrimitive.Anchor;
var PopoverClose = PopoverPrimitive.Close;
var PopoverContent = forwardRef(({ className, align = "center", sideOffset = 6, ...props }, ref) => /* @__PURE__ */ jsx(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 w-72 rounded-md bg-surface p-4 text-foreground shadow-elevation-2 outline-none",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = "PopoverContent";
function FilterPill({
  dimension,
  state,
  onToggleValue,
  onRemove,
  defaultOpen = false
}) {
  const t = useT();
  const [open, setOpen] = useState(defaultOpen);
  const selected = state[dimension.id] ?? [];
  const summary = summarizeValues(dimension, state);
  const isSingle = dimension.mode === "single";
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center rounded-full border border-hairline bg-surface shadow-elevation-1", children: [
    /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: setOpen, children: [
      /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          "aria-label": summary === null ? t("ui.filterbar.pill.chooseAria", { label: dimension.label }) : t("ui.filterbar.pill.editAria", { label: dimension.label, values: summary }),
          className: "inline-flex h-7 items-center gap-1.5 rounded-full ps-3 pe-1.5 text-xs text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
          children: [
            /* @__PURE__ */ jsx("span", { className: "font-medium", children: dimension.label }),
            summary === null ? /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: t("ui.filterbar.pill.choose") }) : /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: t("ui.filterbar.pill.is") }),
              /* @__PURE__ */ jsx("span", { className: "max-w-[16rem] truncate", children: summary })
            ] })
          ]
        }
      ) }),
      /* @__PURE__ */ jsxs(PopoverContent, { align: "start", className: "w-60 p-1.5", children: [
        /* @__PURE__ */ jsx("p", { className: "px-1.5 pb-1 pt-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground", children: dimension.label }),
        /* @__PURE__ */ jsx(
          "ul",
          {
            role: isSingle ? "radiogroup" : "group",
            "aria-label": dimension.label,
            className: "max-h-72 space-y-0.5 overflow-y-auto",
            children: dimension.options.map((option) => {
              const checked = selected.includes(option.value);
              return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
                "button",
                {
                  type: "button",
                  role: isSingle ? "radio" : "checkbox",
                  "aria-checked": checked,
                  onClick: () => {
                    onToggleValue(option.value);
                  },
                  className: cn(
                    "flex w-full items-center gap-2 rounded-sm px-1.5 py-1.5 text-start text-sm text-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                    checked && "bg-primary-subtle"
                  ),
                  children: [
                    /* @__PURE__ */ jsx(
                      Checkbox,
                      {
                        checked,
                        "aria-hidden": "true",
                        tabIndex: -1,
                        className: "pointer-events-none"
                      }
                    ),
                    /* @__PURE__ */ jsx("span", { className: "flex-1 truncate", children: option.label }),
                    option.count !== void 0 ? /* @__PURE__ */ jsx("span", { className: "shrink-0 text-xs tabular-nums text-muted-foreground", children: option.count }) : null
                  ]
                }
              ) }, option.value);
            })
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onRemove,
        "aria-label": t("ui.filterbar.pill.removeAria", { label: dimension.label }),
        className: "me-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        children: /* @__PURE__ */ jsx(
          "svg",
          {
            "aria-hidden": "true",
            viewBox: "0 0 16 16",
            className: "h-3 w-3",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "2",
            strokeLinecap: "round",
            children: /* @__PURE__ */ jsx("path", { d: "M4 4l8 8M12 4l-8 8" })
          }
        )
      }
    )
  ] });
}
function FilterBar({
  dimensions,
  value,
  onChange,
  summary,
  children,
  className
}) {
  const t = useT();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [justAdded, setJustAdded] = useState(null);
  const applied = appliedDimensions(dimensions, value);
  const available = availableDimensions(dimensions, value);
  const count = appliedCount(value);
  const onAdd = (dimension) => {
    setPickerOpen(false);
    setJustAdded(dimension.id);
    onChange(addDimension(value, dimension.id));
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2.5", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
      /* @__PURE__ */ jsxs(Popover, { open: pickerOpen, onOpenChange: setPickerOpen, children: [
        /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            "aria-haspopup": "dialog",
            "aria-expanded": pickerOpen,
            className: "relative inline-flex h-9 items-center gap-2 rounded-sm border border-hairline bg-surface px-3 text-sm font-medium text-foreground shadow-elevation-1 transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
            children: [
              /* @__PURE__ */ jsx(
                "svg",
                {
                  "aria-hidden": "true",
                  viewBox: "0 0 24 24",
                  className: "h-4 w-4",
                  fill: "none",
                  stroke: "currentColor",
                  strokeWidth: "2",
                  strokeLinecap: "round",
                  children: /* @__PURE__ */ jsx("path", { d: "M3 5h18M6 12h12M10 19h4" })
                }
              ),
              t("ui.filterbar.trigger"),
              count > 0 ? /* @__PURE__ */ jsx(
                "span",
                {
                  "aria-hidden": "true",
                  className: "ms-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[0.6875rem] font-semibold text-primary-foreground",
                  children: count
                }
              ) : null
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(PopoverContent, { align: "start", className: "w-72 p-1.5", children: /* @__PURE__ */ jsx(FilterDimensionPicker, { dimensions: available, onAdd }) })
      ] }),
      children
    ] }),
    applied.length > 0 ? /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
      applied.map((dimension) => /* @__PURE__ */ jsx(
        FilterPill,
        {
          dimension,
          state: value,
          defaultOpen: dimension.id === justAdded,
          onToggleValue: (optionValue) => {
            onChange(toggleValue(value, dimension, optionValue));
          },
          onRemove: () => {
            if (justAdded === dimension.id) setJustAdded(null);
            onChange(removeDimension(value, dimension.id));
          }
        },
        dimension.id
      )),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => {
            setJustAdded(null);
            onChange(clearAll());
          },
          className: "inline-flex h-7 items-center rounded-sm px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
          children: t("ui.filterbar.clearAll")
        }
      ),
      summary !== void 0 ? (
        // `<Bdi>` because `summary` is CALLER text and this row mirrors under
        // ar/he. The prop's own doc shows the intended usage as
        // `${shown} of ${total}` — digits are bidi-NEUTRAL, so in an RTL
        // paragraph an unisolated "22 of 104" reorders to "of 104 22", which
        // is what the Storybook locale toolbar renders the moment it is
        // switched to Hebrew. Isolation is the fix rather than a wrapper
        // `dir`, because the summary may legitimately be RTL text itself for
        // an RTL tenant, and `<bdi>` resolves each on its own content.
        /* @__PURE__ */ jsx("span", { "aria-live": "polite", className: "ms-1 text-xs text-muted-foreground", children: /* @__PURE__ */ jsx(Bdi, { children: summary }) })
      ) : null
    ] }) : null,
    /* @__PURE__ */ jsx("span", { className: "sr-only", "aria-live": "polite", children: t("ui.filterbar.appliedCount", { count: String(count) }) })
  ] });
}
var HOVER_QUERY = "(hover: hover) and (pointer: fine)";
function hasMatchMedia() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}
function noUnsubscribe() {
  return void 0;
}
function subscribe(onChange) {
  if (!hasMatchMedia()) return noUnsubscribe;
  const mql = window.matchMedia(HOVER_QUERY);
  mql.addEventListener("change", onChange);
  return () => {
    mql.removeEventListener("change", onChange);
  };
}
function getSnapshot() {
  return hasMatchMedia() ? window.matchMedia(HOVER_QUERY).matches : false;
}
function getServerSnapshot() {
  return false;
}
function useHoverCapable() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = forwardRef(({ className, sideOffset = 6, ...props }, ref) => /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsxs(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 max-w-[210px] rounded-[5px] bg-inverse px-2.5 py-1.5 text-[12.5px] leading-4 text-inverse-foreground shadow-elevation-2",
      className
    ),
    ...props,
    children: [
      props.children,
      /* @__PURE__ */ jsx(TooltipPrimitive.Arrow, { className: "fill-surface" })
    ]
  }
) }));
TooltipContent.displayName = "TooltipContent";
var TRIGGER_CLASS = "inline-flex size-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
var CONTENT_CLASS = "text-xs leading-relaxed text-foreground";
function InfoGlyph() {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className: "size-4",
      children: [
        /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10" }),
        /* @__PURE__ */ jsx("path", { d: "M12 16v-4" }),
        /* @__PURE__ */ jsx("path", { d: "M12 8h.01" })
      ]
    }
  );
}
var InfoTrigger = forwardRef(({ label, className, ...props }, ref) => /* @__PURE__ */ jsx(
  "button",
  {
    ref,
    type: "button",
    "aria-label": label,
    className: cn(TRIGGER_CLASS, className),
    ...props,
    children: /* @__PURE__ */ jsx(InfoGlyph, {})
  }
));
InfoTrigger.displayName = "InfoTrigger";
function InfoHint({
  label,
  children,
  side = "top",
  align = "center",
  contentClassName,
  triggerClassName,
  trigger
}) {
  const hoverCapable = useHoverCapable();
  const triggerNode = trigger !== void 0 ? cloneElement(trigger, { "aria-label": label }) : /* @__PURE__ */ jsx(InfoTrigger, { label, className: triggerClassName });
  if (hoverCapable) {
    return /* @__PURE__ */ jsx(TooltipProvider, { delayDuration: 200, children: /* @__PURE__ */ jsxs(Tooltip, { children: [
      /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: triggerNode }),
      /* @__PURE__ */ jsx(TooltipContent, { side, align, className: cn(CONTENT_CLASS, contentClassName), children })
    ] }) });
  }
  return /* @__PURE__ */ jsxs(Popover, { children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: triggerNode }),
    /* @__PURE__ */ jsx(
      PopoverContent,
      {
        side,
        align,
        role: "note",
        "aria-label": label,
        className: cn("w-72 p-3", CONTENT_CLASS, contentClassName),
        children
      }
    )
  ] });
}
function InlineError({
  message,
  retryLabel,
  onRetry,
  className,
  ...rest
}) {
  const showRetry = onRetry !== void 0 && retryLabel !== void 0;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "alert",
      className: cn(
        "rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger-foreground",
        className
      ),
      ...rest,
      children: [
        /* @__PURE__ */ jsx("p", { children: message }),
        showRetry ? /* @__PURE__ */ jsx(Button, { type: "button", variant: "secondary", size: "sm", className: "mt-2", onClick: onRetry, children: retryLabel }) : null
      ]
    }
  );
}
var FLAT_FILL = {
  white: "#ffffff",
  ink: "#0a0b0d",
  currentColor: "currentColor"
};
function Logo({
  variant = "mark",
  tone = "gradient",
  size = 32,
  title,
  fit = false,
  className
}) {
  const reactId = useId();
  const gradientId = `syncanix-logo-grad-${reactId}`;
  const stops = tone === "bright" ? GRADIENT_STOPS_BRIGHT : GRADIENT_STOPS;
  const flatFill = FLAT_FILL[tone];
  const markUsesGradient = variant !== "mark" || flatFill === void 0;
  const gradientRef = `url(#${gradientId})`;
  const a11y = title ? { role: "img", "aria-label": title } : { "aria-hidden": true };
  const viewBox = variant === "mark" ? fit ? MARK_VIEWBOX_TIGHT : MARK_VIEWBOX : BADGE_VIEWBOX;
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className,
      width: size,
      height: size,
      viewBox,
      xmlns: "http://www.w3.org/2000/svg",
      focusable: "false",
      ...a11y,
      children: [
        title ? /* @__PURE__ */ jsx("title", { children: title }) : null,
        markUsesGradient ? /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx(
          "linearGradient",
          {
            id: gradientId,
            x1: GRADIENT_GEOMETRY.x1,
            y1: GRADIENT_GEOMETRY.y1,
            x2: GRADIENT_GEOMETRY.x2,
            y2: GRADIENT_GEOMETRY.y2,
            gradientUnits: "userSpaceOnUse",
            children: stops.map((s) => /* @__PURE__ */ jsx("stop", { offset: s.offset, stopColor: s.color }, s.offset))
          }
        ) }) : null,
        variant === "badge" ? /* @__PURE__ */ jsx("rect", { width: "100", height: "100", rx: "23", fill: gradientRef }) : null,
        variant === "coin" ? /* @__PURE__ */ jsx("circle", { cx: "50", cy: "50", r: "50", fill: gradientRef }) : null,
        variant === "mark" ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("path", { d: PATH_OUTER, fill: markUsesGradient ? gradientRef : flatFill }),
          /* @__PURE__ */ jsx("path", { d: PATH_INNER, fill: markUsesGradient ? gradientRef : flatFill })
        ] }) : (
          // Badge/coin: white knockout of the mark over the gradient tile.
          /* @__PURE__ */ jsxs("g", { transform: BADGE_FIT_TRANSFORM, fill: "#ffffff", children: [
            /* @__PURE__ */ jsx("path", { d: PATH_OUTER }),
            /* @__PURE__ */ jsx("path", { d: PATH_INNER })
          ] })
        )
      ]
    }
  );
}
var BASE = "inline-flex items-center gap-1.5 rounded-xs border px-2.5 py-0.5 text-xs font-medium";
function MatchCountBadge({
  state,
  label,
  loadingLabel,
  errorLabel,
  capped = false,
  onClick,
  className
}) {
  if (state === "loading") {
    return /* @__PURE__ */ jsx(
      "span",
      {
        role: "status",
        "aria-busy": "true",
        className: cn(
          BASE,
          "animate-pulse border-border bg-muted text-muted-foreground",
          className
        ),
        children: loadingLabel
      }
    );
  }
  if (state === "error") {
    return /* @__PURE__ */ jsx(
      "span",
      {
        role: "status",
        className: cn(BASE, "border-warning/40 bg-warning/10 text-warning-foreground", className),
        children: errorLabel
      }
    );
  }
  const readyClasses = cn(
    BASE,
    capped ? "border-primary/40 bg-primary-subtle text-primary-strong" : "border-border bg-surface text-foreground",
    className
  );
  if (onClick !== void 0) {
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick,
        className: cn(
          readyClasses,
          "hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        ),
        children: label
      }
    );
  }
  return /* @__PURE__ */ jsx("span", { className: readyClasses, children: label });
}
function PageHeader({
  title,
  description,
  actions,
  docsHref,
  docsLabel,
  className,
  ...rest
}) {
  const docsLink = docsHref !== void 0 && docsLabel !== void 0 ? /* @__PURE__ */ jsxs(
    "a",
    {
      href: docsHref,
      target: "_blank",
      rel: "noopener noreferrer",
      className: "text-[13px] text-tertiary underline-offset-2 hover:text-foreground hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
      children: [
        docsLabel,
        " \u2197"
      ]
    }
  ) : null;
  const titleBlock = /* @__PURE__ */ jsxs("div", { className: "flex-1 basis-64 space-y-1", children: [
    /* @__PURE__ */ jsx("h1", { className: "text-h1 text-foreground", children: title }),
    description !== void 0 ? /* @__PURE__ */ jsx("p", { className: "text-sm leading-5 text-muted-foreground", children: description }) : null,
    docsLink
  ] });
  if (actions === void 0) {
    return /* @__PURE__ */ jsxs("header", { className: cn("space-y-1", className), ...rest, children: [
      /* @__PURE__ */ jsx("h1", { className: "text-h1 text-foreground", children: title }),
      description !== void 0 ? /* @__PURE__ */ jsx("p", { className: "text-sm leading-5 text-muted-foreground", children: description }) : null,
      docsLink
    ] });
  }
  return /* @__PURE__ */ jsxs("header", { className: cn("flex flex-wrap items-start justify-between gap-4", className), ...rest, children: [
    titleBlock,
    /* @__PURE__ */ jsx("div", { className: "flex max-w-full shrink-0 flex-wrap items-center justify-end gap-2", children: actions })
  ] });
}
var RISK_STEPS = {
  low: 1,
  medium: 2,
  high: 3,
  critical: 4
};
var TONE = {
  read: "bg-muted text-muted-foreground",
  write: "bg-warning/15 text-warning-foreground",
  destructive: "bg-danger/15 text-danger-foreground"
};
var METER_FILL = {
  read: "bg-muted-foreground/50",
  write: "bg-warning-foreground/70",
  destructive: "bg-danger-foreground/70"
};
var METER_STEPS = [0, 1, 2, 3];
function SafetyChip({
  sideEffect,
  risk,
  actionLabel,
  accessibleName
}) {
  if (!sideEffect) return null;
  const steps = risk ? RISK_STEPS[risk] : 1;
  const name = accessibleName ?? actionLabel;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-1.5 rounded-xs px-1.5 py-0.5 text-xs font-medium",
        TONE[sideEffect]
      ),
      title: name,
      children: [
        /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: actionLabel }),
        risk ? /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "inline-flex items-center gap-px", children: METER_STEPS.map((i) => /* @__PURE__ */ jsx(
          "span",
          {
            className: cn(
              "h-2.5 w-0.5 rounded-full",
              i < steps ? METER_FILL[sideEffect] : "bg-current opacity-20"
            )
          },
          i
        )) }) : null,
        risk ? /* @__PURE__ */ jsx("span", { className: "sr-only", children: name }) : null
      ]
    }
  );
}
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
function ChevronIcon3() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 16 16",
      className: "size-4 opacity-60",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      children: /* @__PURE__ */ jsx("path", { d: "m4 6 4 4 4-4", strokeLinecap: "round", strokeLinejoin: "round" })
    }
  );
}
function CheckIcon2() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 16 16",
      className: "size-4",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.75",
      children: /* @__PURE__ */ jsx("path", { d: "m3.5 8.5 3 3 6-7", strokeLinecap: "round", strokeLinejoin: "round" })
    }
  );
}
var SelectTrigger = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  SelectPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex h-[38px] min-h-[38px] w-full items-center justify-between gap-2 rounded-sm border border-hairline-strong bg-surface px-3 text-sm text-foreground shadow-[inset_0_1px_2px_rgba(10,12,17,0.04)] transition-colors hover:bg-surface-well focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/20 data-[placeholder]:text-tertiary disabled:pointer-events-none disabled:bg-surface-well disabled:text-tertiary dark:shadow-none [@media(pointer:coarse)]:min-h-11",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx(ChevronIcon3, {}) })
    ]
  }
));
SelectTrigger.displayName = "SelectTrigger";
var SelectContent = forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  SelectPrimitive.Content,
  {
    ref,
    position,
    className: cn(
      "z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-hidden rounded-md bg-surface text-foreground shadow-elevation-2",
      position === "popper" && "w-[--radix-select-trigger-width]",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(SelectPrimitive.Viewport, { className: "p-[5px]", children })
  }
) }));
SelectContent.displayName = "SelectContent";
var SelectLabel = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.Label,
  {
    ref,
    className: cn(
      "px-[10px] pb-1 pt-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.105em] text-tertiary",
      className
    ),
    ...props
  }
));
SelectLabel.displayName = "SelectLabel";
var SelectItem = forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  SelectPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex min-h-[34px] w-full cursor-pointer select-none items-center rounded-[5px] py-[7px] pe-8 ps-[10px] text-[13.5px] outline-none transition-colors data-[highlighted]:bg-surface-well data-[disabled]:pointer-events-none data-[disabled]:text-tertiary [@media(pointer:coarse)]:min-h-11",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children }),
      /* @__PURE__ */ jsx("span", { className: "absolute end-2 flex items-center justify-center text-primary-strong", children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon2, {}) }) })
    ]
  }
));
SelectItem.displayName = "SelectItem";
var SelectSeparator = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn("-mx-[5px] my-[5px] h-px bg-hairline", className),
    ...props
  }
));
SelectSeparator.displayName = "SelectSeparator";
var VALUE_OPS = [
  "eq",
  "neq",
  "contains",
  "gt",
  "lt",
  "gte",
  "lte",
  "exists",
  "in"
];
var METRICS = MATCH_RULE_ACTIVITY_METRICS;
var NUMERIC_OPS = /* @__PURE__ */ new Set(["gt", "lt", "gte", "lte"]);
function coerceOp(condition, op) {
  if (op === "exists") return { source: condition.source, key: condition.key, op };
  if (op === "in") {
    return {
      source: condition.source,
      key: condition.key,
      op,
      value: Array.isArray(condition.value) ? condition.value : []
    };
  }
  if (NUMERIC_OPS.has(op)) {
    return {
      source: condition.source,
      key: condition.key,
      op,
      value: typeof condition.value === "number" ? condition.value : 0
    };
  }
  return {
    source: condition.source,
    key: condition.key,
    op,
    value: typeof condition.value === "string" ? condition.value : ""
  };
}
function changeMetric(condition, metric) {
  if (metric === "capability_calls") {
    return { ...condition, metric, capability: condition.capability ?? "", op: "lte", value: 0 };
  }
  const { capability: _dropped, ...rest } = condition;
  return { ...rest, metric, op: "gte", value: Math.max(1, condition.value) };
}
function changeActivityOp(condition, op) {
  return op === "gte" ? { ...condition, op, value: Math.max(1, condition.value) } : { ...condition, op };
}
var CAPABILITY_DATALIST_ID = "rule-builder-capability-ids";
function ActivityFields({
  condition,
  onChange,
  labels,
  disabled,
  capabilityIds
}) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "w-56", children: /* @__PURE__ */ jsxs(
      Select,
      {
        value: condition.metric,
        onValueChange: (metric) => {
          onChange(changeMetric(condition, metric));
        },
        disabled,
        children: [
          /* @__PURE__ */ jsx(SelectTrigger, { "aria-label": labels.metricLabel, children: /* @__PURE__ */ jsx(SelectValue, {}) }),
          /* @__PURE__ */ jsx(SelectContent, { children: METRICS.map((metric) => /* @__PURE__ */ jsx(SelectItem, { value: metric, children: labels.metrics[metric] }, metric)) })
        ]
      }
    ) }),
    condition.metric === "capability_calls" ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("div", { className: "w-48", children: [
        /* @__PURE__ */ jsx(
          Input,
          {
            label: labels.capabilityLabel,
            placeholder: labels.capabilityPlaceholder,
            autoComplete: "off",
            value: condition.capability ?? "",
            disabled,
            list: capabilityIds !== void 0 ? CAPABILITY_DATALIST_ID : void 0,
            onChange: (event) => {
              onChange({ ...condition, capability: event.target.value });
            }
          }
        ),
        capabilityIds !== void 0 && /* @__PURE__ */ jsx("datalist", { id: CAPABILITY_DATALIST_ID, children: capabilityIds.map((id) => /* @__PURE__ */ jsx("option", { value: id }, id)) })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "w-32", children: /* @__PURE__ */ jsxs(
        Select,
        {
          value: condition.op,
          onValueChange: (op) => {
            onChange(changeActivityOp(condition, op));
          },
          disabled,
          children: [
            /* @__PURE__ */ jsx(SelectTrigger, { "aria-label": labels.activityOpLabel, children: /* @__PURE__ */ jsx(SelectValue, {}) }),
            /* @__PURE__ */ jsx(SelectContent, { children: MATCH_RULE_ACTIVITY_OPS.map((op) => /* @__PURE__ */ jsx(SelectItem, { value: op, children: labels.activityOps[op] }, op)) })
          ]
        }
      ) })
    ] }) : null,
    /* @__PURE__ */ jsx("div", { className: "w-28", children: /* @__PURE__ */ jsx(
      Input,
      {
        label: condition.metric === "capability_calls" ? labels.activityTimesLabel : labels.activityAtLeastLabel,
        type: "number",
        min: condition.op === "lte" ? 0 : 1,
        value: String(condition.value),
        disabled,
        onChange: (event) => {
          const floor = condition.op === "lte" ? 0 : 1;
          onChange({
            ...condition,
            value: Math.max(floor, Number(event.target.value) || floor)
          });
        }
      }
    ) }),
    /* @__PURE__ */ jsx("div", { className: "w-32", children: /* @__PURE__ */ jsx(
      Input,
      {
        label: labels.activityWindowLabel,
        type: "number",
        min: 1,
        max: 365,
        value: String(condition.windowDays),
        disabled,
        onChange: (event) => {
          onChange({
            ...condition,
            windowDays: Math.min(365, Math.max(1, Number(event.target.value) || 1))
          });
        }
      }
    ) })
  ] });
}
function ConditionRow({
  condition,
  onChange,
  onRemove,
  labels,
  attributeKeys,
  capabilityIds,
  disabled = false,
  className
}) {
  const isActivity = condition.source === "activity";
  const changeSource = (source) => {
    if (source === "activity") {
      onChange({
        source: "activity",
        metric: "conversations",
        op: "gte",
        value: 1,
        windowDays: 30
      });
      return;
    }
    const nextSource = source;
    if (condition.source === "activity") {
      onChange(
        nextSource === "attr" ? { source: "attr", key: "", op: "eq", value: "" } : { source: "claim", key: "", op: "eq", value: "" }
      );
      return;
    }
    const { source: _previous, ...rest } = condition;
    onChange(nextSource === "attr" ? { ...rest, source: "attr" } : { ...rest, source: "claim" });
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "group",
      "aria-label": labels.conditionRowLabel,
      className: cn(
        "flex flex-wrap items-end gap-2 rounded-md border border-border p-2",
        className
      ),
      children: [
        /* @__PURE__ */ jsx("div", { className: "w-36", children: /* @__PURE__ */ jsxs(Select, { value: condition.source, onValueChange: changeSource, disabled, children: [
          /* @__PURE__ */ jsx(SelectTrigger, { "aria-label": labels.sourceLabel, children: /* @__PURE__ */ jsx(SelectValue, {}) }),
          /* @__PURE__ */ jsxs(SelectContent, { children: [
            /* @__PURE__ */ jsx(SelectItem, { value: "attr", children: labels.sourceAttr }),
            /* @__PURE__ */ jsx(SelectItem, { value: "claim", children: labels.sourceClaim }),
            /* @__PURE__ */ jsx(SelectItem, { value: "activity", children: labels.sourceActivity })
          ] })
        ] }) }),
        isActivity ? /* @__PURE__ */ jsx(
          ActivityFields,
          {
            condition,
            onChange,
            labels,
            disabled,
            capabilityIds
          }
        ) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsxs("div", { className: "w-44", children: [
            /* @__PURE__ */ jsx(
              Input,
              {
                label: labels.keyLabel,
                placeholder: labels.keyPlaceholder,
                autoComplete: "off",
                value: condition.key,
                disabled,
                list: attributeKeys !== void 0 ? "rule-builder-attribute-keys" : void 0,
                onChange: (event) => {
                  onChange({ ...condition, key: event.target.value });
                }
              }
            ),
            attributeKeys !== void 0 && /* @__PURE__ */ jsx("datalist", { id: "rule-builder-attribute-keys", children: attributeKeys.map((key) => /* @__PURE__ */ jsx("option", { value: key }, key)) })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "w-40", children: /* @__PURE__ */ jsxs(
            Select,
            {
              value: condition.op,
              onValueChange: (op) => {
                onChange(coerceOp(condition, op));
              },
              disabled,
              children: [
                /* @__PURE__ */ jsx(SelectTrigger, { "aria-label": labels.opLabel, children: /* @__PURE__ */ jsx(SelectValue, {}) }),
                /* @__PURE__ */ jsx(SelectContent, { children: VALUE_OPS.map((op) => /* @__PURE__ */ jsx(SelectItem, { value: op, children: labels.ops[op] }, op)) })
              ]
            }
          ) }),
          condition.op === "in" ? /* @__PURE__ */ jsx("div", { className: "min-w-48 flex-1", children: /* @__PURE__ */ jsx(
            Input,
            {
              label: labels.valuesLabel,
              placeholder: labels.valuesPlaceholder,
              autoComplete: "off",
              value: Array.isArray(condition.value) ? condition.value.join(", ") : "",
              disabled,
              onChange: (event) => {
                onChange({
                  ...condition,
                  value: event.target.value.split(",").map((entry) => entry.trim()).filter((entry) => entry !== "")
                });
              }
            }
          ) }) : condition.op === "exists" ? null : NUMERIC_OPS.has(condition.op) ? /* @__PURE__ */ jsx("div", { className: "w-32", children: /* @__PURE__ */ jsx(
            Input,
            {
              label: labels.valueLabel,
              type: "number",
              value: typeof condition.value === "number" ? String(condition.value) : "0",
              disabled,
              onChange: (event) => {
                onChange({ ...condition, value: Number(event.target.value) || 0 });
              }
            }
          ) }) : /* @__PURE__ */ jsx("div", { className: "min-w-40 flex-1", children: /* @__PURE__ */ jsx(
            Input,
            {
              label: labels.valueLabel,
              placeholder: labels.valuePlaceholder,
              autoComplete: "off",
              value: typeof condition.value === "string" ? condition.value : "",
              disabled,
              onChange: (event) => {
                onChange({ ...condition, value: event.target.value });
              }
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            variant: "ghost",
            size: "sm",
            "aria-label": labels.removeCondition,
            disabled,
            onClick: onRemove,
            children: /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "\xD7" })
          }
        )
      ]
    }
  );
}

// src/components/RuleBuilder/rule-builder-model.ts
function ruleSections(rule) {
  return { all: rule.all ?? [], any: rule.any ?? [], none: rule.none ?? [] };
}
function defaultCondition() {
  return { source: "attr", key: "", op: "eq", value: "" };
}
function withSection(rule, section, entries) {
  const next = { ...rule };
  if (entries.length === 0) {
    const { [section]: _removed, ...rest } = next;
    return rest;
  }
  if (section === "none") {
    next.none = entries;
  } else if (section === "all") {
    next.all = entries;
  } else {
    next.any = entries;
  }
  return next;
}
function addCondition(rule, section) {
  const sections = ruleSections(rule);
  return withSection(rule, section, [...sections[section], defaultCondition()]);
}
function addGroup(rule, section, mode) {
  const sections = ruleSections(rule);
  const group = mode === "all" ? { all: [defaultCondition()] } : { any: [defaultCondition()] };
  return withSection(rule, section, [...sections[section], group]);
}
function groupConditions(entry) {
  if ("source" in entry) return null;
  return "all" in entry ? entry.all : entry.any;
}
function withGroupConditions(entry, conditions) {
  if ("source" in entry) return entry;
  return "all" in entry ? { all: conditions } : { any: conditions };
}
function updateEntry(rule, section, index, condition, childIndex) {
  const sections = ruleSections(rule);
  const entries = [...sections[section]];
  const current = entries[index];
  if (current === void 0) return rule;
  if (childIndex === void 0) {
    entries[index] = condition;
    return withSection(rule, section, entries);
  }
  const children = groupConditions(current);
  if (children?.[childIndex] === void 0) return rule;
  const nextChildren = [...children];
  nextChildren[childIndex] = condition;
  entries[index] = withGroupConditions(current, nextChildren);
  return withSection(rule, section, entries);
}
function removeEntry(rule, section, index, childIndex) {
  const sections = ruleSections(rule);
  const entries = [...sections[section]];
  const current = entries[index];
  if (current === void 0) return rule;
  if (childIndex === void 0) {
    entries.splice(index, 1);
    return withSection(rule, section, entries);
  }
  const children = groupConditions(current);
  if (children?.[childIndex] === void 0) return rule;
  const nextChildren = children.filter((_, i) => i !== childIndex);
  if (nextChildren.length === 0) {
    entries.splice(index, 1);
  } else {
    entries[index] = withGroupConditions(current, nextChildren);
  }
  return withSection(rule, section, entries);
}
function RuleBuilder({
  value,
  onChange,
  labels,
  attributeKeys,
  capabilityIds,
  disabled = false,
  className
}) {
  const sections = ruleSections(value);
  const renderEntry = (section, entry, index) => {
    if ("source" in entry) {
      return /* @__PURE__ */ jsx(
        ConditionRow,
        {
          condition: entry,
          labels,
          attributeKeys,
          capabilityIds,
          disabled,
          onChange: (condition) => {
            onChange(updateEntry(value, section, index, condition));
          },
          onRemove: () => {
            onChange(removeEntry(value, section, index));
          }
        },
        `${section}-${String(index)}`
      );
    }
    const mode = "all" in entry ? "all" : "any";
    const conditions = "all" in entry ? entry.all : entry.any;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        role: "group",
        "aria-label": mode === "any" ? labels.anyGroupLabel : labels.allGroupLabel,
        className: "space-y-2 rounded-md border border-dashed border-border p-2 ps-3",
        children: [
          /* @__PURE__ */ jsx("p", { className: "font-mono text-label uppercase text-muted-foreground", children: mode === "any" ? labels.anyGroupLabel : labels.allGroupLabel }),
          conditions.map((condition, childIndex) => /* @__PURE__ */ jsx(
            ConditionRow,
            {
              condition,
              labels,
              attributeKeys,
              capabilityIds,
              disabled,
              onChange: (next) => {
                onChange(updateEntry(value, section, index, next, childIndex));
              },
              onRemove: () => {
                onChange(removeEntry(value, section, index, childIndex));
              }
            },
            `${section}-${String(index)}-${String(childIndex)}`
          ))
        ]
      },
      `${section}-group-${String(index)}`
    );
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-5", className), children: [
    /* @__PURE__ */ jsxs("section", { className: "space-y-2", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-sm font-medium", children: labels.matchAllHeading }),
      sections.all.map((entry, index) => renderEntry("all", entry, index)),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2", children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            variant: "secondary",
            size: "sm",
            disabled,
            onClick: () => {
              onChange(addCondition(value, "all"));
            },
            children: labels.addCondition
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            variant: "ghost",
            size: "sm",
            disabled,
            onClick: () => {
              onChange(addGroup(value, "all", "any"));
            },
            children: labels.addAnyGroup
          }
        )
      ] })
    ] }),
    sections.any.length > 0 && /* @__PURE__ */ jsxs("section", { className: "space-y-2", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-sm font-medium", children: labels.matchAnyHeading }),
      sections.any.map((entry, index) => renderEntry("any", entry, index))
    ] }),
    /* @__PURE__ */ jsxs("section", { className: "space-y-2 rounded-md border border-danger/30 bg-danger/5 p-3", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-sm font-medium text-danger-foreground", children: labels.excludeHeading }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: labels.excludeCaption }),
      sections.none.map((entry, index) => renderEntry("none", entry, index)),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          variant: "secondary",
          size: "sm",
          disabled,
          onClick: () => {
            onChange(addCondition(value, "none"));
          },
          children: labels.addExclusion
        }
      )
    ] })
  ] });
}
var SELECTED_INLINE = "bg-surface font-heading text-foreground shadow-[0_0_0_1px_var(--syncanix-hairline)]";
var UNSELECTED_INLINE = "text-muted-foreground hover:text-foreground";
var SELECTED_CARD = "border-primary bg-primary/5 text-foreground font-heading";
var UNSELECTED_CARD = "border-hairline-strong text-muted-foreground hover:bg-surface-well hover:text-foreground";
function SegmentedControl({
  legend,
  options,
  value,
  onChange,
  legendClassName = "mb-1.5 block text-sm font-medium text-foreground",
  hint,
  layout = "inline"
}) {
  const legendId = useId();
  const groupName = useId();
  const hintId = useId();
  const cards = layout === "cards";
  return /* @__PURE__ */ jsxs("fieldset", { children: [
    /* @__PURE__ */ jsx("legend", { id: legendId, className: legendClassName, children: legend }),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: cards ? "grid grid-cols-1 gap-2 sm:grid-cols-2" : "inline-flex flex-wrap items-center gap-0.5 rounded-sm border border-hairline bg-surface-well p-[3px]",
        role: "radiogroup",
        "aria-labelledby": legendId,
        "aria-describedby": hint !== void 0 ? hintId : void 0,
        children: options.map((option) => {
          const id = `${groupName}-${option.value}`;
          const selected = value === option.value;
          return /* @__PURE__ */ jsxs(
            "label",
            {
              htmlFor: id,
              className: cn(
                "flex cursor-pointer transition-colors focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring",
                cards ? "min-h-[44px] items-start gap-2.5 rounded-md border p-3 text-sm font-control" : (
                  // 26px clears WCAG 2.2 SC 2.5.8 (24×24 minimum) on size
                  // alone, and grows to a full 36px target on a coarse
                  // pointer — the compact-affordance-row exception in
                  // `.claude/rules/ui-design.md` §6, not a 44px miss.
                  // 4px, not the 3px chip step: the spec's own segment measures 4, and a
                  // recessed segment wants a slightly softer corner than a chip
                  // printed flat on a panel.
                  "h-[26px] items-center gap-2 rounded-[4px] px-3 text-[13px] font-control [@media(pointer:coarse)]:h-9"
                ),
                cards ? selected ? SELECTED_CARD : UNSELECTED_CARD : selected ? SELECTED_INLINE : UNSELECTED_INLINE
              ),
              children: [
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    id,
                    type: "radio",
                    name: groupName,
                    value: option.value,
                    checked: selected,
                    onChange: () => {
                      onChange(option.value);
                    },
                    className: cn(
                      "accent-primary focus-visible:outline-none",
                      // The dot is hidden — never removed — in two cases, and the
                      // radio itself always stays in the DOM so keyboard and AT
                      // semantics are untouched either way.
                      //
                      // 1. An icon replaces it (existing behaviour).
                      // 2. The `inline` layout, which is the segmented-control case.
                      //    A row of compact segments that each carry a radio dot is
                      //    a radio LIST wearing a segmented control's name: the
                      //    selected segment already states itself with a raised
                      //    surface, a ring and a weight change, so the dot is a
                      //    second, weaker signal whose only real effect is to make
                      //    the control look like every other form on the internet.
                      //
                      // `cards` keeps its dot: those are tall, description-bearing
                      // targets where a marker earns its place, and it is the shape
                      // /account's preference groups are built on.
                      option.icon !== void 0 || !cards ? "sr-only" : "h-4 w-4",
                      cards && option.icon === void 0 && "mt-0.5"
                    )
                  }
                ),
                option.icon !== void 0 ? /* @__PURE__ */ jsx(
                  "span",
                  {
                    "aria-hidden": "true",
                    className: cn("flex shrink-0 items-center", cards && "mt-0.5"),
                    children: option.icon
                  }
                ) : null,
                /* @__PURE__ */ jsxs("span", { className: "flex flex-col gap-0.5", children: [
                  /* @__PURE__ */ jsx("span", { children: option.label }),
                  option.description !== void 0 ? /* @__PURE__ */ jsx("span", { className: "text-xs font-normal text-muted-foreground", children: option.description }) : null
                ] })
              ]
            },
            option.value
          );
        })
      }
    ),
    hint !== void 0 ? /* @__PURE__ */ jsx("p", { id: hintId, className: "mt-1.5 text-xs text-muted-foreground", children: hint }) : null
  ] });
}
function ScrollableRegion({
  "aria-label": ariaLabel,
  className,
  style,
  children
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "region",
      "aria-label": ariaLabel,
      tabIndex: 0,
      className: cn(
        /*
         * `relative` is LOAD-BEARING, not decoration, and it was missing.
         *
         * A scroller whose content is WIDER than it — the whole case this
         * component exists for — leaks that width into an ancestor's
         * `scrollWidth` when the scroller is `position: static`, so the PAGE
         * scrolls sideways while the region itself scrolls correctly. That is
         * the `ui-design.md` §7 failure the region is supposed to prevent,
         * arriving through the component meant to prevent it.
         *
         * Measured in Chromium at 390px with a 544px table inside a 308px
         * region: `main.scrollWidth` 534 against a 390 client. `width: 100%`,
         * `max-width: 100%` + `min-width: 0`, `display: grid` and
         * `isolation: isolate` all left it at 534; `position: relative` and
         * `contain: paint` both returned it to 390. `relative` is the one kept
         * — it is the ordinary idiom, and it also makes the region a containing
         * block for anything a caller positions inside it, where `contain`
         * would silently change stacking and paint behaviour for every caller.
         */
        "relative",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        className
      ),
      style,
      children
    }
  );
}
var skeletonVariants = cva("block animate-pulse bg-muted motion-reduce:animate-none", {
  variants: {
    shape: {
      /** A single line of text — full width, line-height tall. */
      text: "h-4 w-full rounded",
      /** A rectangular block (cards, thumbnails) — size via className. */
      block: "rounded-md",
      /** A circle (avatars) — set equal h/w via className. */
      circle: "rounded-full"
    }
  },
  defaultVariants: {
    shape: "block"
  }
});
var Skeleton = forwardRef(
  ({ className, shape, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      "aria-hidden": "true",
      className: cn(skeletonVariants({ shape }), className),
      ...props
    }
  )
);
Skeleton.displayName = "Skeleton";
var Slider = forwardRef(({ className, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, ...props }, ref) => {
  const thumbCount = props.value?.length ?? props.defaultValue?.length ?? 1;
  return /* @__PURE__ */ jsxs(
    SliderPrimitive.Root,
    {
      ref,
      className: cn(
        "relative flex w-full touch-none select-none items-center data-[disabled]:opacity-50",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx(SliderPrimitive.Track, { className: "relative h-1.5 w-full grow overflow-hidden rounded-full bg-muted", children: /* @__PURE__ */ jsx(SliderPrimitive.Range, { className: "absolute h-full rounded-full bg-primary" }) }),
        Array.from({ length: thumbCount }, (_, i) => /* @__PURE__ */ jsx(
          SliderPrimitive.Thumb,
          {
            "aria-label": ariaLabel,
            "aria-labelledby": ariaLabelledBy,
            className: "block size-4 rounded-full border-2 border-primary bg-background shadow-elevation-1 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none"
          },
          i
        ))
      ]
    }
  );
});
Slider.displayName = "Slider";
var BADGE_KEYS = {
  required: "ui.stepper.badge.required",
  optional: "ui.stepper.badge.optional",
  complete: "ui.stepper.badge.complete"
};
var BADGE_TONES = {
  required: "warning",
  optional: "neutral",
  complete: "success"
};
function Stepper({
  steps,
  activeId,
  onSelect,
  label,
  orientation = "vertical"
}) {
  const t = useT();
  const dir = useDirection();
  const itemRefs = useRef([]);
  const idPrefix = useId();
  const isHorizontal = orientation === "horizontal";
  const activeIndex = steps.findIndex((step) => step.id === activeId);
  const tabStopIndex = activeIndex === -1 ? 0 : activeIndex;
  const forwardKey = isHorizontal ? dir === "rtl" ? "ArrowLeft" : "ArrowRight" : "ArrowDown";
  const backwardKey = isHorizontal ? dir === "rtl" ? "ArrowRight" : "ArrowLeft" : "ArrowUp";
  const handleKeyDown = (event) => {
    if (steps.length === 0) return;
    const focused = itemRefs.current.findIndex((el) => el === document.activeElement);
    if (focused === -1) return;
    let next;
    if (event.key === forwardKey) {
      next = (focused + 1) % steps.length;
    } else if (event.key === backwardKey) {
      next = (focused - 1 + steps.length) % steps.length;
    } else if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = steps.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    itemRefs.current[next]?.focus();
  };
  return /* @__PURE__ */ jsx("nav", { "aria-label": label ?? t("ui.stepper.navLabel"), children: /* @__PURE__ */ jsx("ol", { className: cn("flex", isHorizontal ? "flex-row items-center" : "flex-col"), children: steps.map((step, index) => {
    const isActive = index === activeIndex;
    const isComplete = step.badge === "complete";
    const showDescription = !isHorizontal && isActive && step.description !== void 0;
    const descriptionId = `${idPrefix}-desc-${step.id}`;
    const hasConnector = index < steps.length - 1;
    return /* @__PURE__ */ jsxs(
      "li",
      {
        className: cn(
          "relative",
          // Horizontal: the item is itself a flex row so its trailing
          // connector sits inline with the marker/label. Vertical: breathing
          // room under every step but the last gives the connector its run.
          isHorizontal ? "flex items-center" : hasConnector && "pb-2"
        ),
        children: [
          !isHorizontal && hasConnector ? /* @__PURE__ */ jsx(
            "span",
            {
              "aria-hidden": "true",
              "data-testid": "stepper-connector",
              className: "absolute -bottom-2 top-9 w-px bg-border start-5"
            }
          ) : null,
          /* @__PURE__ */ jsxs(
            "button",
            {
              ref: (el) => {
                itemRefs.current[index] = el;
              },
              type: "button",
              tabIndex: index === tabStopIndex ? 0 : -1,
              "aria-current": isActive ? "step" : void 0,
              "aria-describedby": showDescription ? descriptionId : void 0,
              onKeyDown: handleKeyDown,
              onClick: () => {
                onSelect(step.id);
              },
              className: cn(
                "flex min-h-[44px] rounded-md p-2 text-start text-sm transition-colors",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                // Horizontal lays the marker + label out inline; vertical stacks
                // the label column beside a top-aligned marker.
                isHorizontal ? "items-center gap-2" : "w-full items-start gap-3",
                isActive ? "bg-primary/5 text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              ),
              children: [
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    "data-testid": `stepper-marker-${step.id}`,
                    className: cn(
                      "relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium",
                      isActive && "bg-primary text-primary-foreground",
                      !isActive && isComplete && "bg-success/15 text-success-foreground",
                      !isActive && !isComplete && "border border-border text-muted-foreground"
                    ),
                    children: isComplete && !isActive ? /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "\u2713" }) : step.icon !== void 0 ? /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "flex items-center justify-center", children: step.icon }) : index + 1
                  }
                ),
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: cn(
                      "flex min-w-0",
                      isHorizontal ? "items-center gap-2" : "flex-1 flex-col gap-0.5 pt-0.5"
                    ),
                    children: /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2", children: [
                      /* @__PURE__ */ jsx(
                        "span",
                        {
                          className: cn(
                            "font-medium",
                            isHorizontal ? "whitespace-nowrap" : "break-words"
                          ),
                          children: step.label
                        }
                      ),
                      step.badge !== void 0 ? /* @__PURE__ */ jsx(Badge, { tone: BADGE_TONES[step.badge], className: "shrink-0", children: t(BADGE_KEYS[step.badge]) }) : null
                    ] })
                  }
                )
              ]
            }
          ),
          isHorizontal && hasConnector ? /* @__PURE__ */ jsx(
            "span",
            {
              "aria-hidden": "true",
              "data-testid": "stepper-connector",
              className: "mx-2 h-px w-8 shrink-0 bg-border"
            }
          ) : null,
          showDescription ? /* @__PURE__ */ jsx(
            "p",
            {
              id: descriptionId,
              className: "break-words pb-1 pe-2 ps-11 pt-0.5 text-xs text-muted-foreground",
              children: step.description
            }
          ) : null
        ]
      },
      step.id
    );
  }) }) });
}
var Switch = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  SwitchPrimitive.Root,
  {
    ref,
    className: cn(
      // Bedrock switch — 38×22 with a 18px thumb on a 2px inset. Smaller than
      // the 44×24 it replaces because it now sits in dense option ROWS rather
      // than alone in a form; the row itself is the ≥44px target.
      "peer inline-flex h-[22px] w-[38px] shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-hairline-strong",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(SwitchPrimitive.Thumb, { className: "pointer-events-none block size-[18px] rounded-full bg-white shadow-[0_1px_2px_rgba(10,12,17,0.3)] transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0 rtl:data-[state=checked]:-translate-x-4" })
  }
));
Switch.displayName = "Switch";
function Table({
  wrapperClassName,
  className,
  "aria-label": ariaLabel,
  ...rest
}) {
  return /* @__PURE__ */ jsx(
    ScrollableRegion,
    {
      "aria-label": ariaLabel ?? "",
      className: cn("relative overflow-x-auto rounded-md border border-border", wrapperClassName),
      children: /* @__PURE__ */ jsx("table", { "aria-label": ariaLabel, className: cn("min-w-full text-sm", className), ...rest })
    }
  );
}
function TableHead({
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsx(
    "thead",
    {
      className: cn("bg-surface-well font-mono text-label uppercase text-tertiary", className),
      ...rest
    }
  );
}
function TableBody({
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsx("tbody", { className: cn("divide-y divide-hairline", className), ...rest });
}
function TableRow(props) {
  return /* @__PURE__ */ jsx("tr", { ...props });
}
function TableHeaderCell({
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsx("th", { scope: "col", className: cn("px-4 py-2 text-start font-medium", className), ...rest });
}
function TableCell({
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsx("td", { className: cn("px-4 py-2", className), ...rest });
}
function SortIdleIcon() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 16 16",
      className: "size-3.5 opacity-50",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      children: /* @__PURE__ */ jsx("path", { d: "M5 6.5 8 3.5l3 3M5 9.5l3 3 3-3", strokeLinecap: "round", strokeLinejoin: "round" })
    }
  );
}
function SortDirectionIcon({ direction }) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 16 16",
      className: "size-3.5",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.75",
      children: direction === "ascending" ? /* @__PURE__ */ jsx("path", { d: "M4 10 8 5.5l4 4.5", strokeLinecap: "round", strokeLinejoin: "round" }) : /* @__PURE__ */ jsx("path", { d: "M4 6 8 10.5 12 6", strokeLinecap: "round", strokeLinejoin: "round" })
    }
  );
}
function SortableTableHeaderCell({
  label,
  direction,
  onSort,
  align = "start",
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsx(
    "th",
    {
      scope: "col",
      "aria-sort": direction ?? void 0,
      className: cn("px-0 py-0 text-start font-medium", className),
      ...rest,
      children: /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          onClick: onSort,
          className: cn(
            "flex w-full items-center gap-1 px-4 py-2 text-start font-medium uppercase tracking-wide",
            align === "end" && "justify-end",
            "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
            direction !== null ? "text-foreground" : "text-muted-foreground"
          ),
          children: [
            /* @__PURE__ */ jsx("span", { children: label }),
            direction === null ? /* @__PURE__ */ jsx(SortIdleIcon, {}) : /* @__PURE__ */ jsx(SortDirectionIcon, { direction })
          ]
        }
      )
    }
  );
}
var Tabs = TabsPrimitive.Root;
var TabsList = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.List,
  {
    ref,
    className: cn("flex flex-wrap gap-[22px] border-b border-hairline", className),
    ...props
  }
));
TabsList.displayName = "TabsList";
var tabsTriggerVariants = cva(
  // Bedrock "light line" tabs: no chrome at all until a tab is active, and
  // then a 2px rule under it. The rule is the BRAND GRADIENT — principle 02,
  // "one line of light": the gradient marks where the system is pointing, and
  // a 2px indicator is the only place it is permitted. It is drawn as an
  // ::after so it overlaps the list's own hairline instead of sitting above it.
  "relative inline-flex min-h-[44px] items-center border-0 px-0 pb-[11px] pt-3 text-sm font-control text-muted-foreground transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:text-tertiary data-[state=active]:text-foreground data-[state=active]:after:absolute data-[state=active]:after:inset-x-0 data-[state=active]:after:-bottom-px data-[state=active]:after:h-0.5 data-[state=active]:after:rounded-full data-[state=active]:after:bg-brand-gradient data-[state=active]:after:content-['']"
);
var TabsTrigger = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(TabsPrimitive.Trigger, { ref, className: cn(tabsTriggerVariants(), className), ...props }));
TabsTrigger.displayName = "TabsTrigger";
var TabsContent = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "mt-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
      className
    ),
    ...props
  }
));
TabsContent.displayName = "TabsContent";
var ToastContext = createContext(null);
var AUTO_DISMISS_MS = 4e3;
function ToastProvider({ children, dismissLabel }) {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(0);
  const timers = useRef(/* @__PURE__ */ new Set());
  useEffect(() => {
    const live = timers.current;
    return () => {
      for (const timer of live) clearTimeout(timer);
      live.clear();
    };
  }, []);
  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  const push = useCallback(
    (tone, message, options) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, tone, message, action: options?.action }]);
      const timer = setTimeout(() => {
        timers.current.delete(timer);
        dismiss(id);
      }, options?.durationMs ?? AUTO_DISMISS_MS);
      timers.current.add(timer);
    },
    [dismiss]
  );
  const api = useMemo(
    () => ({
      success: (message, options) => {
        push("success", message, options);
      },
      error: (message, options) => {
        push("error", message, options);
      }
    }),
    [push]
  );
  return /* @__PURE__ */ jsxs(ToastContext.Provider, { value: api, children: [
    children,
    /* @__PURE__ */ jsx("div", { className: "pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:items-end", children: toasts.map((toast) => /* @__PURE__ */ jsxs(
      "div",
      {
        role: toast.tone === "error" ? "alert" : "status",
        "aria-live": toast.tone === "error" ? "assertive" : "polite",
        className: [
          "pointer-events-auto flex w-full max-w-sm items-start gap-[10px] rounded-md bg-inverse p-[13px] text-[13.5px] text-inverse-foreground shadow-elevation-2"
        ].join(" "),
        children: [
          /* @__PURE__ */ jsx(
            "span",
            {
              "aria-hidden": "true",
              className: [
                "mt-[6px] size-1.5 shrink-0 rounded-full",
                toast.tone === "error" ? "bg-danger" : "bg-success"
              ].join(" ")
            }
          ),
          /* @__PURE__ */ jsx("span", { className: "flex-1 font-control", children: toast.message }),
          toast.action !== void 0 && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: () => {
                dismiss(toast.id);
                toast.action?.onAction();
              },
              className: "shrink-0 rounded-xs font-control text-[12.5px] text-accent-300 hover:underline hover:underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              children: toast.action.label
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: () => {
                dismiss(toast.id);
              },
              "aria-label": dismissLabel,
              className: "shrink-0 rounded-xs text-inverse-muted transition-colors hover:text-inverse-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              children: /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "\xD7" })
            }
          )
        ]
      },
      toast.id
    )) })
  ] });
}
function useToast() {
  const ctx = useContext(ToastContext);
  return ctx ?? NOOP_TOAST;
}
var NOOP_TOAST = {
  success: () => {
  },
  error: () => {
  }
};

// src/export/formats.ts
var EXPORT_FORMAT_META = {
  csv: { ext: "csv", mime: "text/csv;charset=utf-8" },
  xlsx: {
    ext: "xlsx",
    mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  },
  png: { ext: "png", mime: "image/png" },
  svg: { ext: "svg", mime: "image/svg+xml;charset=utf-8" },
  json: { ext: "json", mime: "application/json;charset=utf-8" },
  txt: { ext: "txt", mime: "text/plain;charset=utf-8" },
  // Markdown transcript export (W-05) — served by `toTranscriptMarkdown`.
  md: { ext: "md", mime: "text/markdown;charset=utf-8" },
  // PDF: rasterize-to-paginated-A4 via `nodeToPdf` (SYN-182), plugged into this
  // slot without reshaping the handler model. See ADR-0160.
  pdf: { ext: "pdf", mime: "application/pdf" }
};
async function toXlsx(rows, columns) {
  const { default: writeXlsxFile } = await import('write-excel-file');
  const cols = resolveColumns(rows, columns);
  const schema = cols.map((col) => ({
    column: col.header ?? col.key,
    type: String,
    value: (row) => cellToString(row[col.key])
  }));
  return writeXlsxFile(rows, { schema });
}

// src/export/image.ts
async function nodeToPng(el) {
  const { toBlob } = await import('html-to-image');
  const blob = await toBlob(el);
  if (blob === null) {
    throw new Error("nodeToPng: failed to rasterize the node to a PNG blob");
  }
  return blob;
}
function nodeToSvg(el) {
  const isSvgRoot = el instanceof SVGSVGElement || el.tagName.toLowerCase() === "svg";
  const svg = isSvgRoot ? el : pickContentSvg(el);
  if (svg === null) {
    throw new Error("nodeToSvg: no <svg> element found in the provided node");
  }
  const clone = svg.cloneNode(true);
  if (clone.getAttribute("xmlns") === null) {
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  }
  const serialized = new XMLSerializer().serializeToString(clone);
  return `<?xml version="1.0" encoding="UTF-8"?>
${serialized}`;
}
function pickContentSvg(el) {
  const candidates = [...el.querySelectorAll("svg")];
  return candidates.find((svg) => svg.getAttribute("aria-hidden") !== "true") ?? candidates[0] ?? null;
}

// src/export/pdf-chrome.ts
var PRINT_BG = "#ffffff";
var PRINT_INK = "#1f2937";
var PRINT_MUTED = "#525252";
var PRINT_HAIRLINE = "#d4d4d4";
var CAPTURE_PIXEL_RATIO = 2;
var MAX_CAPTURE_DIMENSION_PX = 32e3;
function exceedsCaptureCeiling(widthPx, heightPx) {
  return widthPx * CAPTURE_PIXEL_RATIO > MAX_CAPTURE_DIMENSION_PX || heightPx * CAPTURE_PIXEL_RATIO > MAX_CAPTURE_DIMENSION_PX;
}
function resolveDir(el, override) {
  if (override !== void 0) return override;
  if (typeof window === "undefined") return "ltr";
  return window.getComputedStyle(el).direction === "rtl" ? "rtl" : "ltr";
}
function createLightContainer(dir, widthPx) {
  const container = document.createElement("div");
  container.className = "light";
  container.setAttribute("data-theme", "light");
  container.setAttribute("dir", dir);
  Object.assign(container.style, {
    position: "fixed",
    top: "0",
    transform: "translateX(-100000px)",
    width: `${widthPx}px`,
    background: PRINT_BG,
    pointerEvents: "none",
    zIndex: "-1"
  });
  return container;
}
async function captureChild(host, child) {
  const { toCanvas } = await import('html-to-image');
  host.appendChild(child);
  try {
    const canvas = await toCanvas(child, {
      backgroundColor: PRINT_BG,
      pixelRatio: CAPTURE_PIXEL_RATIO,
      cacheBust: true
    });
    return { dataUrl: canvas.toDataURL("image/png"), width: canvas.width, height: canvas.height };
  } finally {
    host.removeChild(child);
  }
}
function buildHeader(title, subtitle, dir) {
  const wrap = document.createElement("div");
  wrap.setAttribute("dir", dir);
  Object.assign(wrap.style, {
    boxSizing: "border-box",
    width: "100%",
    paddingBottom: "6px",
    borderBottom: `1px solid ${PRINT_HAIRLINE}`,
    background: PRINT_BG,
    color: PRINT_INK,
    fontFamily: "inherit"
  });
  const titleEl = document.createElement("div");
  titleEl.textContent = title;
  Object.assign(titleEl.style, {
    fontSize: "16px",
    fontWeight: "600",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  });
  wrap.appendChild(titleEl);
  if (subtitle !== void 0 && subtitle !== "") {
    const subEl = document.createElement("div");
    subEl.textContent = subtitle;
    subEl.setAttribute("dir", "ltr");
    Object.assign(subEl.style, {
      marginTop: "2px",
      fontFamily: "monospace",
      fontSize: "11px",
      color: PRINT_MUTED,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      unicodeBidi: "isolate"
    });
    wrap.appendChild(subEl);
  }
  return wrap;
}
function buildFooter(dateText, pageText, dir) {
  const wrap = document.createElement("div");
  wrap.setAttribute("dir", dir);
  Object.assign(wrap.style, {
    boxSizing: "border-box",
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    paddingTop: "6px",
    borderTop: `1px solid ${PRINT_HAIRLINE}`,
    background: PRINT_BG,
    color: PRINT_MUTED,
    fontFamily: "inherit",
    fontSize: "10px"
  });
  const start = document.createElement("span");
  start.textContent = dateText;
  const end = document.createElement("span");
  end.textContent = pageText;
  wrap.append(start, end);
  return wrap;
}
function mmHeight(image, widthMm) {
  return image.width > 0 ? widthMm * (image.height / image.width) : 0;
}

// src/export/pdf-layout.ts
var A4_SHORT_MM = 210;
var A4_LONG_MM = 297;
var MARGIN_X_MM = 14;
var MARGIN_TOP_MM = 12;
var MARGIN_BOTTOM_MM = 12;
var HEADER_BAND_MM = 16;
var FOOTER_BAND_MM = 10;
var BAND_GAP_MM = 4;
function computeA4Layout(orientation = "portrait") {
  const pageWidth = orientation === "landscape" ? A4_LONG_MM : A4_SHORT_MM;
  const pageHeight = orientation === "landscape" ? A4_SHORT_MM : A4_LONG_MM;
  const headerTop = MARGIN_TOP_MM;
  const bodyTop = MARGIN_TOP_MM + HEADER_BAND_MM + BAND_GAP_MM;
  const footerTop = pageHeight - MARGIN_BOTTOM_MM - FOOTER_BAND_MM;
  const bodyBottom = footerTop - BAND_GAP_MM;
  return {
    pageWidth,
    pageHeight,
    contentWidth: pageWidth - 2 * MARGIN_X_MM,
    bodyTop,
    bodyBottom,
    contentHeight: bodyBottom - bodyTop,
    marginX: MARGIN_X_MM,
    headerTop,
    footerTop
  };
}
var PAGE_FIT_EPSILON = 1e-6;
function computePdfPages({
  imageWidth,
  imageHeight,
  contentWidth,
  contentHeight,
  bodyTop
}) {
  const scaledWidth = contentWidth;
  const hasRatio = imageWidth > 0 && imageHeight > 0;
  const scaledHeight = hasRatio ? contentWidth * (imageHeight / imageWidth) : 0;
  const pageCount = scaledHeight <= 0 || contentHeight <= 0 ? 1 : Math.max(1, Math.ceil(scaledHeight / contentHeight - PAGE_FIT_EPSILON));
  const offsets = [];
  for (let i = 0; i < pageCount; i += 1) {
    offsets.push(bodyTop - i * contentHeight);
  }
  return { pageCount, scaledWidth, scaledHeight, offsets };
}

// src/export/pdf.ts
var PdfContentTooLargeError = class extends Error {
  code = "pdf-content-too-large";
  constructor(message = "nodeToPdf: content is too large to capture for a PDF (exceeds the canvas ceiling)") {
    super(message);
    this.name = "PdfContentTooLargeError";
  }
};
async function nodeToPdf(el, opts) {
  const dir = resolveDir(el, opts.dir);
  const rect = el.getBoundingClientRect();
  const widthPx = Math.max(1, Math.round(rect.width || el.offsetWidth || 1));
  const heightPx = Math.max(1, Math.round(el.scrollHeight || rect.height || el.offsetHeight || 1));
  if (exceedsCaptureCeiling(widthPx, heightPx)) {
    throw new PdfContentTooLargeError();
  }
  const container = createLightContainer(dir, widthPx);
  document.body.appendChild(container);
  try {
    const bodyClone = el.cloneNode(true);
    bodyClone.style.width = "100%";
    const bodyImage = await captureChild(container, bodyClone);
    const headerImage = await captureChild(container, buildHeader(opts.title, opts.subtitle, dir));
    const layout = computeA4Layout(opts.orientation);
    const pages = computePdfPages({
      imageWidth: bodyImage.width,
      imageHeight: bodyImage.height,
      contentWidth: layout.contentWidth,
      contentHeight: layout.contentHeight,
      bodyTop: layout.bodyTop
    });
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({
      orientation: opts.orientation ?? "portrait",
      unit: "mm",
      format: "a4"
    });
    const headerMm = mmHeight(headerImage, layout.contentWidth);
    for (let i = 0; i < pages.pageCount; i += 1) {
      if (i > 0) doc.addPage();
      doc.addImage(
        bodyImage.dataUrl,
        "PNG",
        layout.marginX,
        pages.offsets[i] ?? layout.bodyTop,
        pages.scaledWidth,
        pages.scaledHeight,
        "syncanix-pdf-body",
        "FAST"
      );
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, layout.pageWidth, layout.bodyTop, "F");
      doc.rect(0, layout.bodyBottom, layout.pageWidth, layout.pageHeight - layout.bodyBottom, "F");
      doc.addImage(
        headerImage.dataUrl,
        "PNG",
        layout.marginX,
        layout.headerTop,
        layout.contentWidth,
        headerMm,
        "syncanix-pdf-header",
        "FAST"
      );
      const footerImage = await captureChild(
        container,
        buildFooter(opts.footerDate, opts.pageLabel(i + 1, pages.pageCount), dir)
      );
      doc.addImage(
        footerImage.dataUrl,
        "PNG",
        layout.marginX,
        layout.footerTop,
        layout.contentWidth,
        mmHeight(footerImage, layout.contentWidth),
        void 0,
        "FAST"
      );
    }
    return doc.output("blob");
  } finally {
    container.remove();
  }
}

// src/export/download.ts
function triggerDownload(data, filename, mime = "text/plain;charset=utf-8") {
  const blob = typeof data === "string" ? new Blob([data], { type: mime }) : data;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// src/lib/field-class.ts
var fieldClass = "rounded-sm border border-hairline-strong bg-surface px-3 text-sm text-foreground shadow-[inset_0_1px_2px_rgba(10,12,17,0.04)] dark:shadow-none transition-colors placeholder:text-tertiary focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/[0.14] disabled:cursor-not-allowed disabled:bg-surface-well disabled:text-tertiary";
var selectFieldClass = `h-[38px] ${fieldClass} [@media(pointer:coarse)]:min-h-11`;
var textareaFieldClass = `py-2 ${fieldClass}`;

// src/lib/focus-ring.ts
var focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
var focusRingField = "focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/[0.14]";
var focusRingFieldWithin = "focus-within:border-ring focus-within:outline-none focus-within:ring-[3px] focus-within:ring-ring/[0.14]";
var focusRingInset = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";
var METHOD_TONE = {
  GET: "bg-success/[0.12] text-success-foreground",
  POST: "bg-info/[0.10] text-info-foreground",
  PATCH: "bg-warning/[0.14] text-warning-foreground",
  PUT: "bg-warning/[0.14] text-warning-foreground",
  DELETE: "bg-danger/[0.11] text-danger-foreground"
};
var ActionChip = forwardRef(
  ({ className, verb, method, ...props }, ref) => /* @__PURE__ */ jsxs(
    "span",
    {
      ref,
      className: cn(
        // `flex-wrap`, and NO `whitespace-nowrap` on the chip itself.
        //
        // The chip used to refuse to wrap at all, which kept the verb and its
        // method glued together — right up to the point where the verb was long
        // enough that the pair could not fit. Measured at 375px on
        // `actionchip--long-content`: the German "Lieferadresse einer
        // bestehenden Bestellung ändern" pushed the document to 401px against a
        // 375px viewport and shoved the method badge off the screen entirely.
        //
        // Truncating instead would be the wrong trade: the plain-language verb
        // IS the meaning here (principle 04 — the human word leads), so cutting
        // it costs the chip the thing it exists to say, and a name a person
        // cannot read is `ui-design.md` §6's failure rather than a tidy one.
        // So the verb wraps and the badge stays whole on the final line.
        "inline-flex flex-wrap items-center gap-1.5 rounded-xs py-0.5 pe-1 ps-2 text-xs font-control",
        METHOD_TONE[method],
        className
      ),
      ...props,
      children: [
        verb,
        /* @__PURE__ */ jsx("span", { className: "whitespace-nowrap rounded-[2px] bg-current/15 px-1 py-px font-mono text-[9.5px] font-semibold", children: method })
      ]
    }
  )
);
ActionChip.displayName = "ActionChip";

// src/components/Avatar/avatar-src.ts
var AUTH0_AVATAR_CDN = "cdn.auth0.com";
var GRAVATAR_DOMAIN = "gravatar.com";
function isHost(host, domain) {
  return host === domain || host.endsWith(`.${domain}`);
}
function resolveAvatarSrc(src) {
  if (src === void 0 || src.trim() === "") return void 0;
  let url;
  try {
    url = new URL(src);
  } catch {
    return src;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return src;
  if (isHost(url.hostname, AUTH0_AVATAR_CDN) && url.pathname.startsWith("/avatars/")) {
    return void 0;
  }
  if (isHost(url.hostname, GRAVATAR_DOMAIN)) {
    url.searchParams.set("d", "404");
    return url.toString();
  }
  return src;
}

// src/components/Avatar/initials.ts
var BRACKETED_QUALIFIER = /[([{（［【][^)\]}）］】]*[)\]}）］】]?/gu;
var NAMEABLE = /[^\s\p{P}]/u;
function nameableInitial(part) {
  for (const char of part) {
    if (NAMEABLE.test(char)) return char;
  }
  return "";
}
function pickInitials(value) {
  const initials = value.split(/\s+/).map(nameableInitial).filter(Boolean);
  if (initials.length === 0) return "";
  const first = initials[0] ?? "";
  const last = initials.length > 1 ? initials[initials.length - 1] ?? "" : "";
  return (first + last).toLocaleUpperCase();
}
function initialsFrom(name) {
  const trimmed = name.trim();
  if (trimmed.length === 0) return "";
  return pickInitials(trimmed.replace(BRACKETED_QUALIFIER, " ")) || pickInitials(trimmed);
}
var SIZE_CLASS2 = {
  sm: "size-6 text-[10px]",
  // 30px at 12px/560 is the spec's own avatar, to the pixel.
  md: "size-[30px] text-xs",
  lg: "size-9 text-[13px]"
};
var RING_CLASS = {
  sm: "p-[2px]",
  md: "p-[3px]",
  lg: "p-[3.5px]"
};
var Avatar = forwardRef(
  ({ className, name, src, size = "md", accent = false, fallback, ...props }, ref) => {
    const [failed, setFailed] = useState(false);
    const resolved = resolveAvatarSrc(src);
    useEffect(() => {
      setFailed(false);
    }, [resolved]);
    const showImage = resolved !== void 0 && !failed;
    const named = name.trim().length > 0;
    return /* @__PURE__ */ jsx(
      "span",
      {
        ref,
        role: named ? "img" : void 0,
        "aria-label": named ? name : void 0,
        "aria-hidden": named ? void 0 : true,
        className: cn(
          "inline-flex shrink-0 select-none items-center justify-center rounded-full",
          SIZE_CLASS2[size],
          // The accent marks WHO — the current user, the active workspace.
          //
          // THE SPEC DRAWS THIS GRADIENT-FILLED, with white initials, and this
          // is the one component where the implementation deliberately differs.
          // The reason is measured rather than assumed, and the measurement is
          // more forgiving than the first pass thought: centred initials on a
          // 30px face only ever sit on the MIDDLE of the sweep (t 0.27–0.73),
          // not its ends, which lifts white from 1.80:1 to 3.11:1. Real, and
          // still short of the 4.5:1 floor for 12px text.
          //
          // What settles it is that NO ink closes the gap. Across that centre
          // band the best white manages is 3.07:1, the darkest system ink
          // 3.30:1, and even pure black only 3.55:1 — the band is a
          // mid-luminance blue, roughly equidistant from both extremes. So
          // "pick a better foreground" has no answer here; the fill has to go.
          //
          // A ring is also the more faithful reading of principle 02 — the
          // gradient is a line of light marking where the system is pointing,
          // not a surface to write on. It is deliberately THICK (see
          // `RING_CLASS`): at 1.5px it read as a thin outline beside the spec's
          // emphatic brand mark, and the point is to keep the mark's presence
          // while giving the letters an opaque surface to sit on.
          accent ? cn("bg-brand-gradient font-heading", RING_CLASS[size]) : "overflow-hidden border border-hairline-strong bg-surface-well font-heading text-muted-foreground",
          className
        ),
        ...props,
        children: /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": "true",
            className: cn(
              "inline-flex size-full items-center justify-center overflow-hidden rounded-full",
              accent && "bg-surface-well text-foreground"
            ),
            children: showImage ? /* @__PURE__ */ jsx(
              "img",
              {
                src: resolved,
                alt: "",
                onError: () => {
                  setFailed(true);
                },
                className: "size-full object-cover",
                referrerPolicy: "no-referrer"
              }
            ) : (
              // `initialsFrom` returns '' for a name it cannot read — an empty
              // string, or one made only of punctuation. An empty circle reads as
              // a failed image load, so fall back to a glyph. The fallback lives
              // here rather than at the call site because an avatar that renders
              // nothing is always wrong, whoever asked for it.
              //
              // A caller-supplied `fallback` takes precedence over both, but only
              // when there are no initials to show: a caller passing one has
              // something better than `?` to say about an ANONYMOUS person, and
              // nothing to say about a named one.
              (initialsFrom(name) || fallback) ?? "?"
            )
          }
        )
      }
    );
  }
);
Avatar.displayName = "Avatar";
function AvatarStack({
  className,
  people,
  max = 4,
  srLabel,
  size = "md",
  ...props
}) {
  const shown = people.slice(0, max);
  const overflow = people.length - shown.length;
  return /* @__PURE__ */ jsxs("div", { role: "img", "aria-label": srLabel, className: cn("flex items-center", className), ...props, children: [
    shown.map((person, index) => /* @__PURE__ */ jsx(
      Avatar,
      {
        name: person.name,
        ...person.src !== void 0 ? { src: person.src } : {},
        size,
        "aria-hidden": "true",
        role: "presentation",
        "aria-label": void 0,
        className: cn("ring-2 ring-surface", index > 0 && "-ms-2")
      },
      `${person.name}-${String(index)}`
    )),
    overflow > 0 ? /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": "true",
        className: cn(
          "-ms-2 inline-flex shrink-0 items-center justify-center rounded-full border border-hairline-strong bg-surface-well font-heading tabular-nums text-muted-foreground ring-2 ring-surface",
          SIZE_CLASS2[size]
        ),
        children: `+${String(overflow)}`
      }
    ) : null
  ] });
}
function DangerZone({ title, actions, className, ...rest }) {
  return /* @__PURE__ */ jsxs(
    "section",
    {
      className: cn(
        "overflow-hidden rounded-lg border border-destructive/30 bg-destructive/[0.03]",
        className
      ),
      ...rest,
      children: [
        /* @__PURE__ */ jsx("div", { className: "border-b border-destructive/20 px-5 py-3.5", children: /* @__PURE__ */ jsx("h3", { className: "font-mono text-label uppercase text-danger-foreground", children: title }) }),
        /* @__PURE__ */ jsx("div", { className: "divide-y divide-destructive/15", children: actions.map((item, index) => /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-start justify-between gap-4 px-5 py-4", children: [
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsx("div", { className: "text-sm font-control text-foreground", children: item.title }),
            /* @__PURE__ */ jsx("p", { className: "mt-1 text-[13px] leading-[18px] text-muted-foreground", children: item.description })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "shrink-0", children: item.action })
        ] }, index)) })
      ]
    }
  );
}
var DELTA_DIRECTIONS = ["up", "down", "flat"];
function DeltaArrow({ direction }) {
  if (direction === "flat") {
    return /* @__PURE__ */ jsx("svg", { "aria-hidden": "true", viewBox: "0 0 12 12", className: "size-3 shrink-0", fill: "none", children: /* @__PURE__ */ jsx("path", { d: "M2.5 6h7", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round" }) });
  }
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 12 12",
      className: cn("size-3 shrink-0", direction === "down" && "rotate-180"),
      fill: "none",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M6 9.75V2.5M6 2.5 2.75 5.75M6 2.5l3.25 3.25",
          stroke: "currentColor",
          strokeWidth: "1.75",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
}
var Delta = forwardRef(
  ({ className, direction, value, polarity = "up-is-good", srLabel, ...props }, ref) => {
    const good = polarity === "up-is-good" ? direction === "up" : direction === "down";
    const tone = direction === "flat" || polarity === "neutral" ? "border border-hairline bg-surface-well text-muted-foreground" : good ? "bg-success/[0.12] text-success-foreground" : "bg-danger/[0.11] text-danger-foreground";
    return /* @__PURE__ */ jsxs(
      "span",
      {
        ref,
        className: cn(
          "inline-flex items-center gap-1 rounded-xs px-[7px] py-0.5 text-xs font-heading leading-4 tabular-nums",
          tone,
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsx(DeltaArrow, { direction }),
          /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: value }),
          /* @__PURE__ */ jsx("span", { className: "sr-only", children: srLabel })
        ]
      }
    );
  }
);
Delta.displayName = "Delta";
var Eyebrow = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "span",
    {
      ref,
      className: cn("font-mono text-label uppercase text-tertiary", className),
      ...props
    }
  )
);
Eyebrow.displayName = "Eyebrow";
var BOX = {
  sm: "size-6 rounded-sm [&>svg]:size-3.5",
  md: "size-8 rounded-md [&>svg]:size-4",
  lg: "size-10 rounded-lg [&>svg]:size-5"
};
var TONE2 = {
  accent: "bg-primary/10 text-primary-strong",
  neutral: "bg-muted text-muted-foreground"
};
var IconTile = forwardRef(
  ({ size = "md", tone = "accent", className, ...props }, ref) => /* @__PURE__ */ jsx(
    "span",
    {
      ref,
      className: cn(
        "inline-flex shrink-0 items-center justify-center",
        BOX[size],
        TONE2[tone],
        className
      ),
      ...props
    }
  )
);
IconTile.displayName = "IconTile";
var COLUMN_CLASS = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 xl:grid-cols-4"
};
var FILLER_WIDE = {
  2: ["sm:block", "sm:hidden"],
  3: ["lg:block", "lg:hidden"],
  4: ["xl:block", "xl:hidden"]
};
function vacancy(n, cols) {
  return (cols - n % cols) % cols;
}
function fillers(count, columns) {
  const mid = vacancy(count, 2);
  const wide = vacancy(count, columns);
  const [show, hide] = FILLER_WIDE[columns];
  return Array.from({ length: Math.max(mid, wide) }, (_, i) => /* @__PURE__ */ jsx(
    "div",
    {
      "aria-hidden": "true",
      "data-metric-sheet-filler": "",
      className: cn(
        "hidden bg-surface",
        i < mid ? "sm:block" : "sm:hidden",
        i < wide ? show : hide
      )
    },
    `filler-${String(i)}`
  ));
}
var MetricSheet = forwardRef(
  ({ className, columns = 4, children, ...props }, ref) => /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      className: cn(
        // `bg-hairline` is the GRID: it shows only through the 1px gaps.
        "grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-hairline bg-hairline shadow-elevation-1",
        COLUMN_CLASS[columns],
        className
      ),
      ...props,
      children: [
        children,
        fillers(Children.toArray(children).length, columns)
      ]
    }
  )
);
MetricSheet.displayName = "MetricSheet";
var MetricTile = forwardRef(
  ({ className, label, value, unit, delta, visual, footnote, action, primary = false, ...props }, ref) => /* @__PURE__ */ jsxs("div", { ref, className: cn("relative bg-surface p-5", className), ...props, children: [
    primary ? /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "absolute inset-x-0 top-0 h-0.5 bg-brand-gradient" }) : null,
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "min-w-0 break-words font-mono text-label uppercase text-tertiary", children: label }),
      action !== void 0 ? /* @__PURE__ */ jsx("span", { className: "shrink-0", children: action }) : null
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "mt-3.5 flex flex-wrap items-baseline gap-x-2.5 gap-y-1", children: [
      /* @__PURE__ */ jsxs("span", { className: "text-metric tabular-nums text-foreground", children: [
        value,
        unit !== void 0 ? /* @__PURE__ */ jsx("span", { className: "text-[22px] text-muted-foreground", children: unit }) : null
      ] }),
      delta
    ] }),
    visual !== void 0 ? /* @__PURE__ */ jsx("div", { className: "mt-4", children: visual }) : null,
    footnote !== void 0 ? /* @__PURE__ */ jsx("div", { className: "mt-2.5 text-[12.5px] leading-[17px] text-tertiary", children: footnote }) : null
  ] })
);
MetricTile.displayName = "MetricTile";
var OptionRow = forwardRef(
  ({ className, title, description, control, note, disabled = false, ...props }, ref) => /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      className: cn(
        "flex items-start justify-between gap-6 py-4",
        disabled && "opacity-60",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
          /* @__PURE__ */ jsx("div", { className: "text-sm font-control text-foreground", children: title }),
          description !== void 0 ? /* @__PURE__ */ jsx("p", { className: "mt-1 text-[13px] leading-[18px] text-muted-foreground", children: description }) : null,
          note !== void 0 ? /* @__PURE__ */ jsx("p", { className: "mt-1.5 text-xs leading-4 text-tertiary", children: note }) : null
        ] }),
        /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center gap-2 pt-0.5", children: control })
      ]
    }
  )
);
OptionRow.displayName = "OptionRow";
var OptionRowGroup = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("divide-y divide-hairline", className), ...props })
);
OptionRowGroup.displayName = "OptionRowGroup";

// src/components/Pagination/pagination-window.ts
function paginationWindow(page, pageCount, radius = 1) {
  if (pageCount <= 0) return [];
  const wanted = /* @__PURE__ */ new Set([1, pageCount]);
  for (let p = page - radius; p <= page + radius; p += 1) {
    if (p >= 1 && p <= pageCount) wanted.add(p);
  }
  const sorted = [...wanted].sort((a, b) => a - b);
  const out = [];
  let previous = 0;
  for (const p of sorted) {
    if (previous !== 0 && p - previous === 2) out.push(previous + 1);
    else if (previous !== 0 && p - previous > 2) out.push("gap");
    out.push(p);
    previous = p;
  }
  return out;
}
function Chevron({ back }) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      viewBox: "0 0 16 16",
      className: cn("size-4 rtl:-scale-x-100", back && "rotate-180"),
      fill: "none",
      "aria-hidden": "true",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "m6 3.5 4.5 4.5L6 12.5",
          stroke: "currentColor",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
}
var STEP = "inline-flex size-8 items-center justify-center rounded-sm border border-hairline-strong bg-surface text-muted-foreground transition-colors hover:bg-surface-well hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:border-hairline disabled:text-tertiary";
function Pagination({
  page,
  pageCount,
  onPageChange,
  label,
  previousLabel,
  nextLabel,
  pageLabel,
  summary,
  className,
  ...rest
}) {
  if (pageCount <= 1 && summary === void 0) return null;
  const items = paginationWindow(page, pageCount);
  return /* @__PURE__ */ jsxs(
    "nav",
    {
      "aria-label": label,
      className: cn("flex flex-wrap items-center justify-between gap-3", className),
      ...rest,
      children: [
        summary !== void 0 ? /* @__PURE__ */ jsx("span", { className: "text-[13px] tabular-nums text-muted-foreground", children: summary }) : /* @__PURE__ */ jsx("span", {}),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: STEP,
              "aria-label": previousLabel,
              disabled: page <= 1,
              onClick: () => {
                onPageChange(page - 1);
              },
              children: /* @__PURE__ */ jsx(Chevron, { back: true })
            }
          ),
          items.map(
            (item, index) => item === "gap" ? /* @__PURE__ */ jsx(
              "span",
              {
                "aria-hidden": "true",
                className: "px-1 text-[13px] text-tertiary",
                children: "\u2026"
              },
              `gap-${String(index)}`
            ) : /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                "aria-current": item === page ? "page" : void 0,
                "aria-label": pageLabel(item),
                onClick: () => {
                  onPageChange(item);
                },
                className: cn(
                  "inline-flex size-8 items-center justify-center rounded-sm text-[13px] tabular-nums transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                  item === page ? "border border-hairline-strong bg-surface font-heading text-foreground shadow-elevation-1" : "text-muted-foreground hover:bg-surface-well hover:text-foreground"
                ),
                children: item
              },
              item
            )
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: STEP,
              "aria-label": nextLabel,
              disabled: page >= pageCount,
              onClick: () => {
                onPageChange(page + 1);
              },
              children: /* @__PURE__ */ jsx(Chevron, { back: false })
            }
          )
        ] })
      ]
    }
  );
}
var panelVariants = cva("overflow-hidden rounded-lg border border-hairline bg-surface", {
  variants: {
    elevation: {
      // The default. A panel sits ON the page — `elevation-1` is a 1px lift in
      // Daylight and a bare hairline ring in Midnight, and that is all a panel
      // ever needs. `flat` exists for a panel already inside another ruled
      // region, where a second shadow would be a seam.
      resting: "shadow-elevation-1",
      flat: "shadow-elevation-0"
    }
  },
  defaultVariants: { elevation: "resting" }
});
var Panel = forwardRef(
  ({ className, elevation, as = "section", ...props }, ref) => {
    const Comp = as;
    return /* @__PURE__ */ jsx(
      Comp,
      {
        ref,
        className: cn(panelVariants({ elevation }), className),
        ...props
      }
    );
  }
);
Panel.displayName = "Panel";
function PanelHeader({
  title,
  description,
  actions,
  headingLevel = 3,
  className,
  children,
  ...rest
}) {
  const Heading = `h${String(headingLevel)}`;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex items-start justify-between gap-4 border-b border-hairline px-5 py-[18px]",
        className
      ),
      ...rest,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
          /* @__PURE__ */ jsx(Heading, { className: "text-h3 text-foreground", children: title }),
          description !== void 0 ? /* @__PURE__ */ jsx("p", { className: "mt-1 text-[13px] leading-[18px] text-muted-foreground", children: description }) : null,
          children
        ] }),
        actions !== void 0 ? /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center gap-2", children: actions }) : null
      ]
    }
  );
}
var panelBodyVariants = cva("", {
  variants: {
    padding: {
      none: "",
      sm: "p-4",
      md: "p-5"
    },
    /**
     * `well` recesses the body into the panel. Reach for it when the body is
     * secondary to the header (a summary under a title, a preview under a
     * form) — it is the alternative to nesting another bordered card, which
     * is the box-in-box Bedrock exists to remove.
     */
    tone: {
      surface: "",
      well: "bg-surface-well"
    }
  },
  defaultVariants: { padding: "md", tone: "surface" }
});
var PanelBody = forwardRef(
  ({ className, padding, tone, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn(panelBodyVariants({ padding, tone }), className), ...props })
);
PanelBody.displayName = "PanelBody";
var PanelFooter = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "flex items-center justify-end gap-2 border-t border-hairline bg-surface-well px-5 py-3.5",
        className
      ),
      ...props
    }
  )
);
PanelFooter.displayName = "PanelFooter";
var PanelSection = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn("border-b border-hairline p-5 last:border-b-0", className),
      ...props
    }
  )
);
PanelSection.displayName = "PanelSection";
function pct(value, max) {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0;
  return Math.min(100, Math.max(0, value / max * 100));
}
var BAR_TONE = {
  accent: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger"
};
var ProgressBar = forwardRef(
  ({ className, value, max = 100, label, valueText, tone = "accent", size = "md", ...props }, ref) => {
    const filled = pct(value, max);
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        role: "progressbar",
        "aria-label": label,
        "aria-valuenow": value,
        "aria-valuemin": 0,
        "aria-valuemax": max,
        "aria-valuetext": valueText,
        className: cn(
          "w-full overflow-hidden rounded-full border border-hairline bg-surface-well",
          size === "sm" ? "h-1.5" : "h-2",
          className
        ),
        ...props,
        children: /* @__PURE__ */ jsx(
          "div",
          {
            className: cn("h-full rounded-full transition-[width] duration-300", BAR_TONE[tone]),
            style: { width: `${String(filled)}%` }
          }
        )
      }
    );
  }
);
ProgressBar.displayName = "ProgressBar";
var SEGMENT_TONE = {
  accent: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  // The neutral slice is the hairline itself — "the rest", not a fifth status.
  neutral: "bg-hairline-strong"
};
var SegmentedMeter = forwardRef(
  ({ className, segments, srLabel, showLegend = true, ...props }, ref) => {
    const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
    return /* @__PURE__ */ jsxs("div", { ref, className: cn("flex flex-col gap-2.5", className), ...props, children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          role: "img",
          "aria-label": srLabel,
          className: "flex h-2 w-full overflow-hidden rounded-full border border-hairline bg-surface-well",
          children: segments.map((segment) => /* @__PURE__ */ jsx(
            "span",
            {
              className: SEGMENT_TONE[segment.tone],
              style: { width: `${String(pct(segment.value, total))}%` }
            },
            segment.label
          ))
        }
      ),
      showLegend ? /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground", children: segments.map((segment) => /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5", children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": "true",
            className: cn("size-[7px] rounded-[2px]", SEGMENT_TONE[segment.tone])
          }
        ),
        segment.label
      ] }, segment.label)) }) : null
    ] });
  }
);
SegmentedMeter.displayName = "SegmentedMeter";
var RING_TONE = {
  accent: "stroke-primary",
  success: "stroke-success",
  warning: "stroke-warning",
  danger: "stroke-danger"
};
function ProgressRing({
  className,
  value,
  max = 100,
  label,
  valueText,
  centerText,
  size = 44,
  tone = "accent",
  ...props
}) {
  const filled = pct(value, max);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "progressbar",
      "aria-label": label,
      "aria-valuenow": value,
      "aria-valuemin": 0,
      "aria-valuemax": max,
      "aria-valuetext": valueText,
      className: cn("relative inline-flex shrink-0 items-center justify-center", className),
      style: { width: size, height: size },
      ...props,
      children: [
        /* @__PURE__ */ jsxs("svg", { viewBox: "0 0 40 40", className: "-rotate-90", width: size, height: size, "aria-hidden": "true", children: [
          /* @__PURE__ */ jsx(
            "circle",
            {
              cx: "20",
              cy: "20",
              r: "16",
              className: "fill-none stroke-hairline-strong",
              strokeWidth: "4"
            }
          ),
          /* @__PURE__ */ jsx(
            "circle",
            {
              cx: "20",
              cy: "20",
              r: "16",
              pathLength: 100,
              strokeDasharray: `${String(filled)} 100`,
              strokeLinecap: "round",
              strokeWidth: "4",
              className: cn("fill-none transition-[stroke-dasharray] duration-300", RING_TONE[tone])
            }
          )
        ] }),
        centerText !== void 0 ? /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": "true",
            className: "absolute max-w-[calc(100%-10px)] truncate text-center text-[11px] font-heading leading-none tabular-nums text-foreground",
            children: centerText
          }
        ) : null
      ]
    }
  );
}
function SaveBar({
  dirty,
  message,
  discard,
  save,
  className,
  ...rest
}) {
  if (!dirty) return null;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "status",
      "aria-live": "polite",
      className: cn(
        "sticky bottom-0 z-20 -mx-5 mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-hairline bg-surface px-5 py-3 shadow-elevation-2",
        className
      ),
      ...rest,
      children: [
        /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2 text-[13px] font-control text-foreground", children: [
          /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "size-1.5 shrink-0 rounded-full bg-warning" }),
          message
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          discard,
          save
        ] })
      ]
    }
  );
}
function SectionHeader({
  title,
  hint,
  actions,
  headingLevel = 2,
  className,
  ...rest
}) {
  const Heading = `h${String(headingLevel)}`;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b-2 border-foreground pb-2.5",
        className
      ),
      ...rest,
      children: [
        /* @__PURE__ */ jsx(Heading, { className: "text-h2 text-foreground", children: title }),
        hint !== void 0 ? /* @__PURE__ */ jsx("span", { className: "min-w-0 flex-1 text-[13px] text-tertiary", children: hint }) : /* @__PURE__ */ jsx("span", { className: "flex-1" }),
        actions !== void 0 ? /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center gap-2", children: actions }) : null
      ]
    }
  );
}
function SelectionBand({
  count,
  label,
  actions,
  className,
  ...rest
}) {
  if (count <= 0) return null;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "status",
      "aria-live": "polite",
      className: cn(
        "flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/20 bg-primary/[0.06] px-4 py-2.5",
        className
      ),
      ...rest,
      children: [
        /* @__PURE__ */ jsx("span", { className: "text-[13px] font-control tabular-nums text-primary-strong", children: label }),
        /* @__PURE__ */ jsx("div", { className: "flex flex-wrap items-center gap-2", children: actions })
      ]
    }
  );
}
var statusChipVariants = cva(
  // `whitespace-nowrap` because a chip is ONE object. In a narrow status
  // column "Pending analysis" wrapped to two lines inside its own rounded box,
  // which reads as a rendering fault rather than a state. Letting the chip set
  // the column's minimum width is the right trade: the column is showing
  // exactly one thing, and it should be wide enough to show it.
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-xs px-[9px] py-[3px] text-[12.5px] font-control leading-4",
  {
    variants: {
      status: {
        healthy: "bg-success/[0.12] text-success-foreground",
        watch: "bg-warning/[0.14] text-warning-foreground",
        risk: "bg-danger/[0.11] text-danger-foreground",
        live: "bg-info/[0.10] text-info-foreground",
        draft: "border border-hairline bg-surface-well text-muted-foreground"
      }
    },
    defaultVariants: { status: "healthy" }
  }
);
var statusDotVariants = cva("size-1.5 shrink-0 rounded-full", {
  variants: {
    status: {
      healthy: "bg-success",
      watch: "bg-warning",
      risk: "bg-danger",
      live: "bg-info",
      draft: "hidden"
    }
  },
  defaultVariants: { status: "healthy" }
});
var StatusChip = forwardRef(
  ({ className, status, label, pulse = false, ...props }, ref) => /* @__PURE__ */ jsxs("span", { ref, className: cn(statusChipVariants({ status }), className), ...props, children: [
    /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": "true",
        className: cn(statusDotVariants({ status }), pulse && "motion-safe:animate-pulse")
      }
    ),
    label
  ] })
);
StatusChip.displayName = "StatusChip";

// src/components/RunStatusCard/RunStatusCard.types.ts
var RUN_STATUS_PHASES = [
  "WAITING_FOR_BROWSER",
  "READING",
  "PRESSING",
  "PROBING",
  "FINISHING",
  "PAUSED",
  "STOPPED",
  "COMPLETE",
  "FAILED"
];
var PHASE_CHIP = {
  WAITING_FOR_BROWSER: "draft",
  READING: "live",
  PRESSING: "live",
  PROBING: "live",
  FINISHING: "live",
  PAUSED: "watch",
  STOPPED: "draft",
  COMPLETE: "healthy",
  FAILED: "risk"
};
var RUN_STATUS_IN_FLIGHT = /* @__PURE__ */ new Set([
  "WAITING_FOR_BROWSER",
  "READING",
  "PRESSING",
  "PROBING",
  "FINISHING"
]);
var RUN_PAUSE_REASONS = [
  "LOGIN_WALL",
  "SESSION_EXPIRED",
  "BOT_CHALLENGE",
  "BACKGROUND_TAB",
  "CEILING_PAGES",
  "CEILING_TIME",
  "TAB_CLOSED"
];
var ACTIVE_PHASES = /* @__PURE__ */ new Set([
  "READING",
  "PRESSING",
  "PROBING",
  "FINISHING"
]);
function RunHeader({
  phase,
  phaseLabel,
  headline,
  growing
}) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-x-3 gap-y-2", children: [
    /* @__PURE__ */ jsx(StatusChip, { status: PHASE_CHIP[phase], label: phaseLabel, pulse: ACTIVE_PHASES.has(phase) }),
    /* @__PURE__ */ jsxs("p", { className: "min-w-0 flex-1 text-body text-foreground", children: [
      /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: headline }),
      growing !== void 0 ? /* @__PURE__ */ jsx("span", { className: "ms-1.5 inline-flex align-middle", children: /* @__PURE__ */ jsx(InfoHint, { label: growing.hintLabel, children: growing.hint }) }) : null
    ] })
  ] });
}
function RunProgress({
  phase,
  progress
}) {
  const total = progress.visited + progress.queued;
  const indeterminate = RUN_STATUS_IN_FLIGHT.has(phase) && (phase === "WAITING_FOR_BROWSER" || total === 0);
  if (indeterminate) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        role: "progressbar",
        "aria-label": progress.label,
        "aria-busy": "true",
        "data-indeterminate": "",
        className: "h-2 w-full overflow-hidden rounded-full border border-hairline bg-surface-well",
        children: /* @__PURE__ */ jsx("div", { className: "h-full w-1/3 rounded-full bg-primary/60 motion-safe:animate-pulse" })
      }
    );
  }
  return /* @__PURE__ */ jsx(
    ProgressBar,
    {
      value: progress.visited,
      max: total,
      label: progress.label,
      valueText: progress.formatCounter(progress.visited, total)
    }
  );
}
function RunNotice({ notice }) {
  return /* @__PURE__ */ jsx(
    Callout,
    {
      tone: notice.tone,
      title: notice.title,
      body: notice.body,
      action: notice.action !== void 0 ? /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          variant: "secondary",
          size: "sm",
          onClick: notice.action.onSelect,
          disabled: notice.action.disabled,
          children: notice.action.label
        }
      ) : void 0
    }
  );
}
function RunTotals({
  totals
}) {
  return /* @__PURE__ */ jsx(MetricSheet, { columns: 4, className: "shadow-none", children: totals.map((total) => /* @__PURE__ */ jsx(
    MetricTile,
    {
      label: total.label,
      value: total.value,
      delta: total.delta !== void 0 ? /* @__PURE__ */ jsx(
        Delta,
        {
          direction: total.delta.direction,
          value: total.delta.value,
          srLabel: total.delta.srLabel,
          ...total.delta.polarity !== void 0 ? { polarity: total.delta.polarity } : {}
        }
      ) : void 0
    },
    total.label
  )) });
}
function RunCoverage({ coverage }) {
  return /* @__PURE__ */ jsxs("p", { className: "flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted-foreground", children: [
    /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: coverage.pagesLine }),
    /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "\xB7" }),
    /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: coverage.endpointsLine }),
    /* @__PURE__ */ jsx(InfoHint, { label: coverage.hintLabel, children: coverage.hint })
  ] });
}
function RunActionButton({
  action,
  variant
}) {
  return /* @__PURE__ */ jsx(
    Button,
    {
      type: "button",
      variant,
      onClick: action.onSelect,
      disabled: action.disabled,
      className: cn(variant === "ghost" && "px-3"),
      children: action.label
    }
  );
}
function RunStatusCard({
  phase,
  pauseReason,
  phaseLabel,
  headline,
  progress,
  doing,
  liveness,
  notice,
  totals,
  coverage,
  provenance,
  eta,
  namingPending,
  actions,
  failure,
  locked,
  className
}) {
  const lockNotice = locked !== void 0 ? {
    tone: "info",
    title: locked.title,
    body: locked.body,
    ...locked.view === void 0 ? {} : { action: locked.view }
  } : void 0;
  const shownNotice = lockNotice ?? notice;
  const showActions = locked === void 0 && actions !== void 0 && phase !== "FAILED";
  const showTotals = phase !== "WAITING_FOR_BROWSER";
  return /* @__PURE__ */ jsxs(
    "section",
    {
      "aria-label": phaseLabel,
      "data-phase": phase,
      "data-pause-reason": pauseReason,
      className: cn(
        "space-y-5 rounded-lg border border-hairline bg-surface p-5 shadow-elevation-1",
        className
      ),
      children: [
        /* @__PURE__ */ jsx(
          RunHeader,
          {
            phase,
            phaseLabel,
            headline,
            growing: progress.growing
          }
        ),
        phase === "FAILED" && failure !== void 0 ? /* @__PURE__ */ jsx(
          ErrorState,
          {
            headingLevel: 2,
            title: failure.title,
            description: failure.description,
            className: "min-h-0 py-6",
            ...locked === void 0 ? {
              action: /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap justify-center gap-2", children: [
                /* @__PURE__ */ jsx(RunActionButton, { action: failure.retry, variant: "primary" }),
                /* @__PURE__ */ jsx(RunActionButton, { action: failure.seeKept, variant: "secondary" })
              ] })
            } : {}
          }
        ) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(RunProgress, { phase, progress }),
          /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
            /* @__PURE__ */ jsx("p", { className: "text-body text-foreground", children: doing }),
            liveness !== void 0 ? /* @__PURE__ */ jsx("p", { "aria-live": "polite", className: "text-[13px] tabular-nums text-muted-foreground", children: liveness }) : null,
            eta !== void 0 ? /* @__PURE__ */ jsx("p", { className: "text-[13px] tabular-nums text-muted-foreground", children: eta }) : null
          ] })
        ] }),
        shownNotice !== void 0 ? /* @__PURE__ */ jsx(RunNotice, { notice: shownNotice }) : null,
        showTotals ? /* @__PURE__ */ jsx(RunTotals, { totals }) : null,
        coverage !== void 0 ? /* @__PURE__ */ jsx(RunCoverage, { coverage }) : null,
        namingPending !== void 0 ? /* @__PURE__ */ jsxs("p", { className: "text-[13px] text-muted-foreground", children: [
          /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: namingPending.text }),
          namingPending.link !== void 0 ? /* @__PURE__ */ jsx("span", { className: "ms-1", children: namingPending.link }) : null
        ] }) : null,
        /* @__PURE__ */ jsx("p", { className: "text-[12.5px] text-tertiary", children: provenance }),
        locked?.stop !== void 0 ? /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-2", children: /* @__PURE__ */ jsx(RunActionButton, { action: locked.stop, variant: "secondary" }) }) : null,
        showActions ? /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2", children: [
          actions.continue !== void 0 ? /* @__PURE__ */ jsx(RunActionButton, { action: actions.continue, variant: "primary" }) : null,
          actions.copyLink !== void 0 ? /* @__PURE__ */ jsx(RunActionButton, { action: actions.copyLink, variant: "secondary" }) : null,
          actions.stop !== void 0 ? /* @__PURE__ */ jsx(RunActionButton, { action: actions.stop, variant: "secondary" }) : null,
          actions.viewReport !== void 0 ? /* @__PURE__ */ jsx(
            RunActionButton,
            {
              action: actions.viewReport,
              variant: actions.continue === void 0 && phase === "COMPLETE" ? "primary" : "ghost"
            }
          ) : null
        ] }) : null
      ]
    }
  );
}
function ChevronDown() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": "true",
      viewBox: "0 0 16 16",
      className: "size-4",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.75",
      children: /* @__PURE__ */ jsx("path", { d: "m4 6 4 4 4-4", strokeLinecap: "round", strokeLinejoin: "round" })
    }
  );
}
var SplitButton = forwardRef(
  ({
    label,
    type = "button",
    onPrimary,
    menuLabel,
    items,
    disabled = false,
    variant = "primary",
    size = "md",
    className
  }, ref) => {
    const [open, setOpen] = useState(false);
    const onPrimaryKeyDown = (event) => {
      if (event.key === "ArrowDown" && !disabled) {
        event.preventDefault();
        setOpen(true);
      }
    };
    return /* @__PURE__ */ jsxs("div", { role: "group", className: cn("inline-flex items-stretch", className), children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          ref,
          type,
          variant,
          size,
          disabled,
          onClick: onPrimary,
          onKeyDown: onPrimaryKeyDown,
          className: "rounded-e-none",
          children: label
        }
      ),
      /* @__PURE__ */ jsx(
        DropdownMenu,
        {
          open,
          onOpenChange: setOpen,
          modal: false,
          align: "end",
          items,
          trigger: /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              variant,
              size: size === "lg" ? "icon-lg" : "icon",
              disabled,
              "aria-label": menuLabel,
              className: cn(
                "rounded-s-none border-s-0",
                // The hairline between the halves. Painted with the label ink
                // at low alpha so it reads on both variants and both themes.
                "shadow-[inset_1px_0_0_0_rgb(255_255_255/0.28)] rtl:shadow-[inset_-1px_0_0_0_rgb(255_255_255/0.28)]",
                variant === "secondary" && "shadow-[inset_1px_0_0_0_var(--syncanix-hairline-strong)] rtl:shadow-[inset_-1px_0_0_0_var(--syncanix-hairline-strong)]"
              ),
              children: /* @__PURE__ */ jsx(ChevronDown, {})
            }
          )
        }
      )
    ] });
  }
);
SplitButton.displayName = "SplitButton";
function StepMarker({ state, index }) {
  if (state === "done") {
    return /* @__PURE__ */ jsx("span", { className: "inline-flex size-[22px] shrink-0 items-center justify-center rounded-full bg-success text-white", children: /* @__PURE__ */ jsx("svg", { viewBox: "0 0 16 16", className: "size-3", fill: "none", "aria-hidden": "true", children: /* @__PURE__ */ jsx(
      "path",
      {
        d: "m3.5 8.5 3 3 6-7",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ) }) });
  }
  if (state === "current") {
    return (
      // A ring rather than a fill: the current step is where you ARE, and a
      // solid accent disc reads as another completed step in a different hue.
      /* @__PURE__ */ jsx("span", { className: "inline-flex size-[22px] shrink-0 items-center justify-center rounded-full border-2 border-primary bg-surface text-[11px] font-heading tabular-nums text-primary-strong", children: index + 1 })
    );
  }
  return /* @__PURE__ */ jsx("span", { className: "inline-flex size-[22px] shrink-0 items-center justify-center rounded-full border border-hairline-strong bg-surface-well text-[11px] font-heading tabular-nums text-tertiary", children: index + 1 });
}
function Steps({
  steps,
  doneLabel,
  currentLabel,
  todoLabel,
  className,
  ...rest
}) {
  const stateLabel = {
    done: doneLabel,
    current: currentLabel,
    todo: todoLabel
  };
  return /* @__PURE__ */ jsx("ol", { className: cn("flex flex-col", className), ...rest, children: steps.map((step, index) => {
    const last = index === steps.length - 1;
    return /* @__PURE__ */ jsxs(
      "li",
      {
        "aria-current": step.state === "current" ? "step" : void 0,
        className: "relative flex gap-3 pb-5 last:pb-0",
        children: [
          !last ? /* @__PURE__ */ jsx(
            "span",
            {
              "aria-hidden": "true",
              className: cn(
                "absolute start-[10px] top-[26px] w-px",
                // Stops exactly at the next marker's top edge.
                "bottom-[-4px]",
                step.state === "done" ? "bg-success/40" : "bg-hairline-strong"
              )
            }
          ) : null,
          /* @__PURE__ */ jsx(StepMarker, { state: step.state, index }),
          /* @__PURE__ */ jsx("div", { className: "min-w-0 flex-1 pt-0.5", children: /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3", children: [
            /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
              /* @__PURE__ */ jsx("span", { className: "sr-only", children: `${stateLabel[step.state]}: ` }),
              /* @__PURE__ */ jsx(
                "span",
                {
                  className: cn(
                    "text-sm",
                    step.state === "todo" ? "text-muted-foreground" : "font-control text-foreground"
                  ),
                  children: step.title
                }
              ),
              step.detail !== void 0 ? /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-[12.5px] leading-[17px] text-tertiary", children: step.detail }) : null
            ] }),
            step.action !== void 0 ? /* @__PURE__ */ jsx("span", { className: "shrink-0", children: step.action }) : null
          ] }) })
        ]
      },
      index
    );
  }) });
}
var MARKER_TONE = {
  neutral: "border-hairline-strong bg-surface-well text-muted-foreground",
  accent: "border-primary/30 bg-primary/10 text-primary-strong",
  success: "border-success/30 bg-success/10 text-success-foreground",
  warning: "border-warning/30 bg-warning/10 text-warning-foreground",
  danger: "border-danger/30 bg-danger/10 text-danger-foreground"
};
function Timeline({ items, className, ...rest }) {
  return /* @__PURE__ */ jsx("ol", { className: cn("flex flex-col", className), ...rest, children: items.map((item, index) => {
    const last = index === items.length - 1;
    return /* @__PURE__ */ jsxs("li", { className: "relative flex gap-3 pb-4 last:pb-0", children: [
      !last ? /* @__PURE__ */ jsx(
        "span",
        {
          "aria-hidden": "true",
          className: "absolute bottom-[-4px] start-[13px] top-7 w-px bg-hairline"
        }
      ) : null,
      /* @__PURE__ */ jsx(
        "span",
        {
          "aria-hidden": "true",
          className: cn(
            "inline-flex size-7 shrink-0 items-center justify-center rounded-full border",
            MARKER_TONE[item.tone ?? "neutral"]
          ),
          children: item.icon
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1 pt-1", children: [
        /* @__PURE__ */ jsx("div", { className: "text-[13.5px] leading-[19px] text-muted-foreground", children: item.content }),
        /* @__PURE__ */ jsx("div", { className: "mt-1 text-xs text-tertiary", children: item.meta })
      ] })
    ] }, index);
  }) });
}
var Well = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "rounded-md border border-hairline bg-surface-well px-4 py-3.5 text-muted-foreground",
        className
      ),
      ...props
    }
  )
);
Well.displayName = "Well";

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, ActionChip, ActivityStrip, ApprovalQueue, AudiencePicker, Avatar, AvatarStack, BADGE_FIT_TRANSFORM, BADGE_VIEWBOX, Badge, Bdi, BrandLoader, Button, Callout, CapabilitySummaryCard, Card, Checkbox, ColorPicker, DELTA_DIRECTIONS, DangerZone, Delta, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DiffCard, DonutGauge, Drawer, DrawerBody, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger, DropdownMenu, EXPORT_FORMAT_META, EmptyState, ErrorState, ExportMenu, Eyebrow, FilterBar, GRADIENT_GEOMETRY, GRADIENT_STOPS, GRADIENT_STOPS_BRIGHT, I18nProvider, IconTile, InfoHint, InlineError, Input, Logo, MARK_VIEWBOX, MARK_VIEWBOX_TIGHT, MatchCountBadge, MetricSheet, MetricTile, MiniBarChart, OptionRow, OptionRowGroup, PATH_INNER, PATH_OUTER, PHASE_CHIP, PageHeader, Pagination, Panel, PanelBody, PanelFooter, PanelHeader, PanelSection, PdfContentTooLargeError, Popover, PopoverAnchor, PopoverClose, PopoverContent, PopoverTrigger, ProgressBar, ProgressRing, RUN_PAUSE_REASONS, RUN_STATUS_PHASES, RuleBuilder, RunStatusCard, SafetyChip, SaveBar, ScrollableRegion, SectionHeader, SegmentBar, SegmentedControl, SegmentedMeter, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue, SelectionBand, Skeleton, Slider, SortableTableHeaderCell, Sparkline, SplitButton, StatusChip, Stepper, Steps, Switch, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, Timeline, ToastProvider, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, Well, addCondition, addDimension, addGroup, appliedCount, appliedDimensions, availableDimensions, badgeVariants, buttonVariants, calloutVariants, cardVariants, clearAll, cn, computeA4Layout, computePdfPages, defaultCondition, fieldClass, focusRing, focusRingField, focusRingFieldWithin, focusRingInset, initialsFrom, interpolate, matchesFilters, nodeToPdf, nodeToPng, nodeToSvg, paginationWindow, panelBodyVariants, panelVariants, removeDimension, removeEntry, resolveAvatarSrc, ruleSections, searchDimensions, selectFieldClass, skeletonVariants, statusChipVariants, summarizeValues, tabsTriggerVariants, textareaFieldClass, toXlsx, toggleValue, triggerDownload, updateEntry, useHoverCapable, useLocale, useT, useToast };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map